import { transforms } from "@react-thermal-printer/image";
import recycleLogo from "assets/images/recycle_logo3.svg";
import {
  Barcode,
  Br,
  Cashdraw,
  Cut,
  Image,
  Line,
  QRCode,
  Raw,
  Row,
  Text,
  TextSize,
} from "react-thermal-printer";
import { getFromLocalStorage } from "../../../../../../utils/localStorageHelpers";
import React, { Fragment } from "react";

// react-thermal-printer's Row only accepts a string or an element for left/right — anything else
// (undefined/null/number/array) throws inside Row.print and kills the whole receipt
const rowText = (value, fallback = " ") =>
  value === null || value === undefined || value === "" ? fallback : String(value);

// template_002_01 prints the variant name in place of the product name, falling back to the
// product name when an item has no variant name.
const getItemName = (item, useVariantName = false) =>
  useVariantName ? item?.variantName || item?.productName : item?.productName;

const renderSlabItems = (slab, slabItems, itemSno = false, useVariantName = false) => {
  return (
    <>
      {slab && <Text bold={true}>{`          ${slab}`}</Text>}
      {slabItems?.map(
        (e, index) =>
          e.voidStatus == null && (
            <Fragment key={index}>
              <Text>{`${
                itemSno ? `${index + 1}`.padEnd(4) : e?.hsnCode?.slice(0, 4).padEnd(4) || `N/A`
              }${!!e?.cess ? "*" : " "} ${
                localStorage.getItem("retailType") === "RESTAURANT"
                  ? `${e?.productName?.slice(0, 36)} (${e?.variantName?.slice(0, 36)})`
                  : `${getItemName(e, useVariantName)?.slice(0, 36)}`
              }`}</Text>
              {e?.addonProduct?.length > 0 && (
                <Text>
                  AddOn: {`${e?.addonProduct?.map((addOn) => addOn?.itemName).join(", ")}`}
                </Text>
              )}
              <Text>{`            ${(e?.returnedQuantity ?? e?.quantityBySpecs ?? e?.quantity)?.toString().padStart(3)}${e?.mrp
                ?.toString()
                .slice(0, 7)
                .padStart(7)}${e?.sellingPrice
                ?.toString()
                .slice(0, 9)
                .padStart(
                  (e?.returnedQuantity ?? e?.quantityBySpecs ?? e?.quantity)?.toString()?.includes(".") &&
                    (e?.returnedQuantity ?? e?.quantityBySpecs ?? e?.quantity)?.toString().split(".")[1]?.length > 2
                    ? 5
                    : 9
                )} ${(e?.returnedSubTotal ?? e?.subTotal)?.toString().slice(0, 9).padStart(9) || `N/A`}`}</Text>
            </Fragment>
          )
      )}
    </>
  );
};

const renderReturnedSlabItems = (slab, slabItems, itemSno = false, useVariantName = false) => {
  return (
    <>
      {slab && <Text bold={true}>{`          ${slab}`}</Text>}
      {slabItems?.map(
        (e, index) =>
          e.voidStatus != null && (
            <Fragment key={index}>
              <Text>{`${
                itemSno ? `${index + 1}`.padEnd(4) : e?.hsnCode?.slice(0, 4).padEnd(4) || `N/A`
              }${!!e?.cess ? "*" : " "} ${getItemName(e, useVariantName)?.slice(0, 36).padEnd(36)}`}</Text>
              <Text>{`             ${(e?.returnedQuantity ?? e?.voidQuantity ?? e?.quantityBySpecs ?? e?.quantity)?.toString().padStart(3)}${e?.mrp
                ?.toString()
                .slice(0, 7)
                .padStart(7)}${e?.sellingPrice?.toString().slice(0, 9).padStart(9)} ${
                (e?.returnedSubTotal ?? e?.subTotal)?.toString().slice(0, 9).padStart(9) || `N/A`
              }`}</Text>
            </Fragment>
          )
      )}
    </>
  );
};

// template_001_02 variant of renderSlabItems: no MRP / Rate columns, a running serial number in
// place of the HSN code, and the whole row on a single line. Dropping MRP (7) and Rate (9) frees
// enough width to fit Qty and Value beside the item name instead of on a second line.
const SNO_WIDTH_002_02 = 4;
const NAME_WIDTH_002_02 = 20;
const QTY_WIDTH_002_02 = 6;
const VALUE_WIDTH_002_02 = 10;
// Item name starts after the serial number and the cess marker.
const NAME_COL_002_02 = SNO_WIDTH_002_02 + 2;

export const ITEM_HEADER_002_02 = `${"S.NO. Items".padEnd(
  NAME_COL_002_02 + NAME_WIDTH_002_02
)}${"Qty".padStart(QTY_WIDTH_002_02)}${"Value".padStart(VALUE_WIDTH_002_02)}`;

// Wraps on word boundaries, falling back to a hard split for words wider than the column.
const wrapName002_02 = (name) => {
  const words = (name || "").split(" ").filter(Boolean);
  const lines = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= NAME_WIDTH_002_02) {
      current = candidate;
      continue;
    }
    if (current) lines.push(current);
    let remaining = word;
    while (remaining.length > NAME_WIDTH_002_02) {
      lines.push(remaining.slice(0, NAME_WIDTH_002_02));
      remaining = remaining.slice(NAME_WIDTH_002_02);
    }
    current = remaining;
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [""];
};

// Names longer than the name column wrap onto indented continuation lines beneath the row, so
// Qty and Value always stay on the first line. Text renders embedded newlines as-is.
const buildItemLine002_02 = ({ sno, cess, name, qty, value }) => {
  const chunks = wrapName002_02(name);
  const qtyText = qty === null || qty === undefined ? "" : qty.toString();
  const valueText = value === null || value === undefined ? `N/A` : value.toString().slice(0, 9);
  const firstLine = `${`${sno}`.padEnd(SNO_WIDTH_002_02)}${cess ? "*" : " "} ${chunks[0].padEnd(
    NAME_WIDTH_002_02
  )}${qtyText.padStart(QTY_WIDTH_002_02)}${valueText.padStart(VALUE_WIDTH_002_02)}`;
  const wrappedLines = chunks.slice(1).map((chunk) => `${"".padEnd(NAME_COL_002_02)}${chunk}`);
  return [firstLine, ...wrappedLines].join("\n");
};

// Serial numbers must run continuously across every GST slab and skip voided rows, so the
// counter is owned by the caller and shared between slabs rather than using each map index.
const createSnoCounter002_02 = () => ({ next: 1 });

const renderSlabItems002_02 = (slab, slabItems, sno = createSnoCounter002_02()) => {
  return (
    <>
      {slab && <Text bold={true}>{`          ${slab}`}</Text>}
      {slabItems?.map(
        (e, index) =>
          e.voidStatus == null && (
            <Fragment key={index}>
              <Text>
                {buildItemLine002_02({
                  sno: sno.next++,
                  cess: !!e?.cess,
                  name:
                    localStorage.getItem("retailType") === "RESTAURANT"
                      ? `${e?.productName?.slice(0, 36)} (${e?.variantName?.slice(0, 36)})`
                      : `${e?.productName?.slice(0, 36)}`,
                  qty: e?.returnedQuantity ?? e?.quantityBySpecs ?? e?.quantity,
                  value: e?.returnedSubTotal ?? e?.subTotal,
                })}
              </Text>
              {e?.addonProduct?.length > 0 && (
                <Text>
                  AddOn: {`${e?.addonProduct?.map((addOn) => addOn?.itemName).join(", ")}`}
                </Text>
              )}
            </Fragment>
          )
      )}
    </>
  );
};

const renderReturnedSlabItems002_02 = (slab, slabItems, sno = createSnoCounter002_02()) => {
  return (
    <>
      {slab && <Text bold={true}>{`          ${slab}`}</Text>}
      {slabItems?.map(
        (e, index) =>
          e.voidStatus != null && (
            <Fragment key={index}>
              <Text>
                {buildItemLine002_02({
                  sno: sno.next++,
                  cess: !!e?.cess,
                  name: `${e?.productName?.slice(0, 36)}`,
                  qty:
                    e?.returnedQuantity ?? e?.voidQuantity ?? e?.quantityBySpecs ?? e?.quantity,
                  value: e?.returnedSubTotal ?? e?.subTotal,
                })}
              </Text>
            </Fragment>
          )
      )}
    </>
  );
};

const renderSlabItems004 = (slabItems, itemSno = false) => {
  return (
    <>
      {slabItems?.map(
        (e, index) =>
          e.voidStatus == null && (
            <>
              <Text>
                {(() => {
                  // Define fixed column widths.
                  const descWidth = 16;
                  const numbersWidth = 22;

                  // Limit the product name to 36 characters and split into chunks.
                  const name = e?.productName?.slice(0, 36) || "";
                  const chunks = name?.match(new RegExp(`.{1,${descWidth}}`, "g")) || [];

                  // Format the numeric fields.
                  const mrpField = (e?.mrp?.toString().slice(0, 7) || "").padStart(6);
                  const quantityField = ((e?.returnedQuantity ?? e?.quantityBySpecs ?? e?.quantity)?.toString() || "").padStart(6);
                  const subTotalField = ((e?.returnedSubTotal ?? e?.subTotal)?.toString().slice(0, 9) || "N/A").padStart(8);
                  const numbersLineRaw = `${mrpField} ${quantityField} ${subTotalField}`;

                  // Center the numbers horizontally within the numbers column.
                  const numbersPadded = numbersLineRaw
                    .padStart(Math.floor((numbersWidth + numbersLineRaw.length) / 2))
                    .padEnd(numbersWidth);

                  const centerIndex = Math.floor((chunks.length - 1) / 2);

                  // Build the final multi-line string.
                  let result = "";
                  for (let i = 0; i < chunks.length; i++) {
                    const descLine = chunks[i].padEnd(descWidth);

                    const rightColumn =
                      i === centerIndex ? numbersPadded : " ".repeat(numbersWidth);

                    result += descLine + rightColumn;
                    if (i < chunks?.length - 1) result += "\n";
                  }

                  return result + "\n";
                })()}
              </Text>
            </>
          )
      )}
    </>
  );
};

const renderSlabItems006 = (slabItems, itemSno = false) => {
  // Word-wrap helper function
  const wrapTextByWords = (text, width) => {
    const words = text.split(" ");
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
      if ((currentLine + (currentLine ? " " : "") + word).length <= width) {
        currentLine += (currentLine ? " " : "") + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });

    if (currentLine) lines.push(currentLine);
    return lines;
  };

  const sanitizeForPrinter = (text = "") => {
    return text
      .replace(/[–—−]/g, "-")
      .replace(/[""]/g, '"')
      .replace(/['']/g, "'")
      .replace(/[™©®]/g, "")
      .replace(/[^\x00-\x7F]/g, "");
  };

  return (
    <>
      {slabItems?.map(
        (e, index) =>
          e?.voidStatus == null && (
            <React.Fragment key={index}>
              {/* Main Product - RENDERED FIRST */}
              <Text>
                {(() => {
                  const descWidth = 16;
                  const numbersWidth = 22;

                  const name = sanitizeForPrinter(
                    `${e?.productName || ""}${e?.variantName ? ` (${e.variantName})` : ""}`.slice(
                      0,
                      45
                    )
                  );
                  const chunks = wrapTextByWords(name, descWidth);

                  const sellingPrice = (e?.sellingPrice?.toString().slice(0, 7) || "").padStart(6);
                  const quantityField = ((e?.returnedQuantity ?? e?.quantityBySpecs ?? e?.quantity)?.toString() || "").padStart(6);
                  const subTotalField = (
                    e?.taxType === "EXCLUSIVE"
                      ? e?.mrpTotal?.toString().slice(0, 9) || "N/A"
                      : (e?.returnedSubTotal ?? e?.subTotal)?.toString().slice(0, 9) || "N/A"
                  ).padStart(8);
                  const numbersLineRaw = `${sellingPrice} ${quantityField} ${subTotalField}`;

                  const numbersPadded = numbersLineRaw
                    .padStart(Math.floor((numbersWidth + numbersLineRaw.length) / 2))
                    .padEnd(numbersWidth);

                  const centerIndex = Math.floor((chunks.length - 1) / 2);

                  let result = "";
                  for (let i = 0; i < chunks.length; i++) {
                    const descLine = chunks[i].padEnd(descWidth);
                    const rightColumn =
                      i === centerIndex ? numbersPadded : " ".repeat(numbersWidth);
                    result += descLine + rightColumn;
                    if (i < chunks.length - 1) result += "\n";
                  }

                  return result + "\n";
                })()}
              </Text>

              {/* Addon Products - RENDERED AFTER MAIN PRODUCT */}
              {e?.addonProduct?.length > 0 &&
                e.addonProduct.map((addOn, addonIndex) => (
                  <Text key={addonIndex}>
                    {(() => {
                      const descWidth = 16;
                      const numbersWidth = 22;

                      // Prefix addon name with "AddOn:"
                      const addonText = sanitizeForPrinter(`AddOn: ${addOn?.itemName || ""}`);
                      const chunks = wrapTextByWords(addonText, descWidth);

                      // Format addon numeric fields
                      const addonPrice = (
                        addOn?.sellingPrice?.toString().slice(0, 7) || ""
                      ).padStart(6);
                      const addonQty = (addOn?.quantity?.toString() || "").padStart(6);
                      const addonSubTotal = (
                        addOn?.subTotal?.toString().slice(0, 9) || "N/A"
                      ).padStart(8);
                      const numbersLineRaw = `${addonPrice} ${addonQty} ${addonSubTotal}`;

                      const numbersPadded = numbersLineRaw
                        .padStart(Math.floor((numbersWidth + numbersLineRaw.length) / 2))
                        .padEnd(numbersWidth);

                      const centerIndex = Math.floor((chunks.length - 1) / 2);

                      let result = "";
                      for (let i = 0; i < chunks.length; i++) {
                        const descLine = chunks[i].padEnd(descWidth);
                        const rightColumn =
                          i === centerIndex ? numbersPadded : " ".repeat(numbersWidth);
                        result += descLine + rightColumn;
                        if (i < chunks.length - 1) result += "\n";
                      }

                      return result;
                    })()}
                    {"\n"}
                  </Text>
                ))}
            </React.Fragment>
          )
      )}
    </>
  );
};

const sanitizeForPrinter = (text = "") => {
  return text
    .replace(/[–—−]/g, "-")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[™©®]/g, "")
    .replace(/[^\x00-\x7F]/g, "");
};

const formatSlot = (startTime, endTime) => {
  if (!startTime || !endTime) return null;

  const startDate = new Date(startTime + (startTime?.endsWith("Z") ? "" : "Z"));
  const endDate = new Date(endTime + (endTime?.endsWith("Z") ? "" : "Z"));

  const startIST = startDate.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const endIST = endDate.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const dateIST = startDate.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
  });

  const dayIST = startDate.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "long",
  });

  return `Slot: ${startIST}-${endIST}, ${dateIST}, ${dayIST}`;
};

const renderSlabItems007 = (slabItems = []) => {
  return (
    <>
      {/* Reset line spacing to printer default */}
      <Raw data={Uint8Array.from([0x1b, 0x33, 0x01])} />

      {slabItems.map((e, index) => {
        // != matches both null and undefined — returned items carry undefined, and a strict
        // !== null check here silently skipped every line on a return receipt
        if (e?.voidStatus != null) return null;

        const descWidth = 16;
        const numbersWidth = 22;

        const productName = String(e?.productName ?? "").slice(0, 45);
        const variantName = String(e?.variantName ?? "").slice(0, 45);

        const name = sanitizeForPrinter(
          productName && variantName
            ? `${productName} (${variantName})`
            : productName || variantName
        );

        // ✅ Word-wrap logic (replaces old regex chunking)
        const wrapTextByWords = (text, width) => {
          const words = text.split(" ");
          const lines = [];
          let currentLine = "";

          words.forEach((word) => {
            if ((currentLine + (currentLine ? " " : "") + word).length <= width) {
              currentLine += (currentLine ? " " : "") + word;
            } else {
              lines.push(currentLine);
              currentLine = word;
            }
          });

          if (currentLine) lines.push(currentLine);
          return lines;
        };

        const chunks = wrapTextByWords(name, descWidth);

        const sellingPrice = (e?.sellingPrice?.toString().slice(0, 7) || "").padStart(6);
        const quantityField = ((e?.returnedQuantity ?? e?.quantityBySpecs ?? e?.quantity)?.toString() || "").padStart(6);
        const subTotalField = (
          e?.taxType === "EXCLUSIVE"
            ? (e?.mrpTotal?.toString() || "N/A").slice(0, 9)
            : ((e?.returnedSubTotal ?? e?.subTotal)?.toString() || "N/A").slice(0, 9)
        ).padStart(8);

        const numbersLineRaw = `${sellingPrice} ${quantityField} ${subTotalField}`;
        const numbersPadded = numbersLineRaw
          .padStart(Math.floor((numbersWidth + numbersLineRaw.length) / 2))
          .padEnd(numbersWidth);

        const centerIndex = Math.floor((chunks.length - 1) / 2);
        let lines = [];

        chunks.forEach((chunk, i) => {
          const desc = chunk.padEnd(descWidth);
          const right = i === centerIndex ? numbersPadded : " ".repeat(numbersWidth);
          lines.push(desc + right);
        });

        if (e?.addonProduct?.length > 0) {
          const addonText = sanitizeForPrinter(
            `AddOn: ${e.addonProduct.map((a) => a.itemName).join(", ")}`
          );

          // ✅ Also use same word-wrap logic for addons
          const addonChunks = wrapTextByWords(addonText, descWidth);

          addonChunks.forEach((line) => {
            lines.push(line.padEnd(descWidth) + " ".repeat(numbersWidth));
          });
        }

        return (
          <React.Fragment key={index}>
            <Text>{lines.join("\n")}</Text>
            {/* 🟦 Slightly reduced feed: ESC J 12 (≈ ¾ line) */}
            <Raw data={Uint8Array.from([0x1b, 0x4a, 0x0c])} />
          </React.Fragment>
        );
      })}
    </>
  );
};

const renderReturnedSlabItems004 = (slabItems, itemSno = false) => {
  return (
    <>
      {slabItems?.map((e, index) => (
        <>
          <Text>
            {(() => {
              // Define fixed column widths.
              const descWidth = 16;
              const numbersWidth = 22;

              // Limit the product name to 36 characters and split into chunks.
              const name = e?.productName?.slice(0, 36) || "";
              const chunks = name?.match(new RegExp(`.{1,${descWidth}}`, "g")) || [];

              // Format the numeric fields.
              const mrpField = (e?.mrp?.toString().slice(0, 7) || "").padStart(6);
              const quantityField = ((e?.returnedQuantity ?? e?.voidQuantity ?? e?.quantityBySpecs ?? e?.quantity)?.toString() || "").padStart(6);
              const subTotalField = ((e?.returnedSubTotal ?? e?.subTotal)?.toString().slice(0, 9) || "N/A").padStart(8);
              const numbersLineRaw = `${mrpField} ${quantityField} ${subTotalField}`;

              // Center the numbers horizontally within the numbers column.
              const numbersPadded = numbersLineRaw
                .padStart(Math.floor((numbersWidth + numbersLineRaw.length) / 2))
                .padEnd(numbersWidth);

              const centerIndex = Math.floor((chunks.length - 1) / 2);

              // Build the final multi-line string.
              let result = "";
              for (let i = 0; i < chunks.length; i++) {
                const descLine = chunks[i].padEnd(descWidth);

                const rightColumn = i === centerIndex ? numbersPadded : " ".repeat(numbersWidth);

                result += descLine + rightColumn;
                if (i < chunks?.length - 1) result += "\n";
              }
            })()}
          </Text>
        </>
      ))}
    </>
  );
};

const renderSlabItems005 = (slabItems, itemSno = false) => {
  return (
    <>
      {slabItems?.map(
        (e, index) =>
          e.voidStatus == null && (
            <Text
              key={index}
              style={{
                marginBottom: 10, // Space between ITEMS
                lineHeight: 15, // Tight spacing between LINES of same item
                fontFamily: "monospace", // Critical for alignment
                includeFontPadding: false, // Removes hidden padding
              }}
            >
              {(() => {
                const descWidth = 12;
                const numbersWidth = 22;
                const totalWidth = descWidth + numbersWidth;

                const name = e?.productName?.slice(0, 24) || "";
                const chunks = name?.match(new RegExp(`.{1,${descWidth}}`, "g")) || [name];

                // Format numbers
                const mrp = (
                  "  " + (Number.isInteger(e?.mrp) ? e.mrp.toString() : e?.mrp?.toFixed(2))
                ).padEnd(7);

                const quantity = ((e?.returnedQuantity ?? e?.quantityBySpecs ?? e?.quantity)?.toString() || "").padStart(3);
                const subtotal = (
                  "  " +
                  (Number.isInteger((e?.returnedSubTotal ?? e?.subTotal)) ? (e?.returnedSubTotal ?? e?.subTotal).toString() : (e?.returnedSubTotal ?? e?.subTotal)?.toFixed(2))
                ).padEnd(9);

                const numbersLine = `${mrp} ${quantity} ${subtotal}`.padEnd(numbersWidth);

                const centerIndex = Math.floor(chunks.length / 2);

                return chunks
                  .map((chunk, i) => {
                    const isCenterLine = i === centerIndex;
                    const left = chunk.padEnd(descWidth, " "); // Fill remaining space
                    const right = isCenterLine ? numbersLine : "";
                    return left + right;
                  })
                  .join("\n");
              })()}
            </Text>
          )
      )}
    </>
  );
};

export const renderStoreDetails = (retailDetails, store004 = false) => {
  return (
    <>
      <Text align="center" bold={true} size={{ width: 2, height: 2 }}>
        {retailDetails?.displayName || "Store Name"}
      </Text>
      <Text align="center">{retailDetails?.address?.addressLine1 || "AddressLine1"}</Text>
      <Text align="center">{retailDetails?.address?.addressLine2 || "AddressLine2"}</Text>

      {!store004 ? (
        <Text align="center">
          Phone: {retailDetails?.contacts?.map(({ phoneNo }) => phoneNo).join(" / ") || "PhoneNo"}
          {retailDetails?.gst ? `  GSTIN: ${retailDetails.gst}` : ""}
        </Text>
      ) : (
        <Text align="center">
          Phone: {retailDetails?.contacts?.map(({ phoneNo }) => phoneNo).join(" / ") || "PhoneNo"}
        </Text>
      )}

      {!store004 && retailDetails?.fssaiNumber && (
        <Text align="center">{`FSSAI: ${retailDetails?.fssaiNumber}`}</Text>
      )}
      <Line />
    </>
  );
};

export const renderStoreDetails006 = (retailDetails, imgBase64) => {
  return (
    <>
      {imgBase64 && <Image src={imgBase64} align="center" width={50} />}

      <Text align="center" bold={true} size={{ width: 2, height: 2 }}>
        {retailDetails?.displayName || "Store Name"}
      </Text>
      {retailDetails?.address?.addressLine1 && (
        <Text align="center">{retailDetails.address.addressLine1}</Text>
      )}

      {retailDetails?.address?.addressLine2 && (
        <Text align="center">{retailDetails.address.addressLine2}</Text>
      )}

      <Text align="center">
        Phone: {retailDetails?.contacts?.map(({ phoneNo }) => phoneNo).join(" / ") || "PhoneNo"}
        {retailDetails?.gst ? `  GSTIN: ${retailDetails.gst}` : ""}
      </Text>

      {retailDetails?.fssaiNumber && (
        <Text align="center">{`FSSAI: ${retailDetails?.fssaiNumber}`}</Text>
      )}
      <Line />
    </>
  );
};

export const renderStoreDetails007 = (retailDetails, imgBase64) => {
  return (
    <>
      {imgBase64 && <Image src={imgBase64} align="center" width={50} />}

      <Text align="center" bold={true} size={{ width: 1, height: 2 }}>
        {retailDetails?.displayName || "Store Name"}
      </Text>
      {retailDetails?.address?.addressLine1 && (
        <Text align="center">{retailDetails.address.addressLine1}</Text>
      )}

      {retailDetails?.address?.addressLine2 && (
        <Text align="center">{retailDetails.address.addressLine2}</Text>
      )}

      <Text align="center">
        Phone: {retailDetails?.contacts?.map(({ phoneNo }) => phoneNo).join(" / ") || "PhoneNo"}
      </Text>
      {retailDetails?.gst && <Text align="center">{`GSTIN: ${retailDetails?.gst}`}</Text>}

      {retailDetails?.fssaiNumber && (
        <Text align="center">{`FSSAI: ${retailDetails?.fssaiNumber}`}</Text>
      )}
      <Line />
    </>
  );
};

export const renderStoreDetails008 = (retailDetails, imgBase64) => {
  return (
    <>
      {imgBase64 && (
        <Image
          src={imgBase64}
          align="center"
          width={560} // near full width of thermal paper at 203 DPI
        />
      )}
      <Text> </Text>

      {retailDetails?.address?.addressLine1 && (
        <Text align="center">{retailDetails.address.addressLine1}</Text>
      )}

      {retailDetails?.address?.addressLine2 && (
        <Text align="center">{retailDetails.address.addressLine2}</Text>
      )}

      <Text align="center">
        Phone: {retailDetails?.contacts?.map(({ phoneNo }) => phoneNo).join(" / ") || "PhoneNo"}
      </Text>
      {retailDetails?.gst && <Text align="center">{`GSTIN: ${retailDetails?.gst}`}</Text>}

      {retailDetails?.fssaiNumber && (
        <Text align="center">{`FSSAI: ${retailDetails?.fssaiNumber}`}</Text>
      )}
      <Line />
    </>
  );
};

// ODS/b2c orders can be take away as well as delivery; print the right mode label
const getOrderModeLabel = (baseOrderResponse) =>
  baseOrderResponse?.subOrderType === "TAKE_AWAY" ? "TAKEAWAY" : "DELIVERY";

export const renderOrderHeader = (
  isB2cOrder,
  isDeliveryOrder,
  reprint,
  orderId,
  invoiceId,
  baseOrderResponse,
  baseDetails,
  date,
  returnId,
  printType,
  store004 = false
) => {
  const { firstName } = getFromLocalStorage("retailUserDetails");
  return (
    <>
      <Text align="center" bold={true}>
        {printType === "credit-note" ? "DEBIT NOTE" : "TAX INVOICE"}
        {(isB2cOrder || isDeliveryOrder) && ` (${getOrderModeLabel(baseOrderResponse)})`}
        {reprint && ` (Reprinted by ${firstName})`}
      </Text>

      <Line />

      <Text>{`Cust No: ${baseOrderResponse?.mobileNumber || "N/A"}`}</Text>

      <Line />
      {printType == "credit-note" && (
        <Row left={returnId} right={baseOrderResponse?.orderId || "N/A"} />
      )}

      <>
        <Row
          left={`Bill No ${isB2cOrder || isDeliveryOrder ? orderId : invoiceId}`}
          right={`${baseOrderResponse?.updatedByName || " "}/${
            baseOrderResponse?.licenseName || " "
          }`}
        />

        <Row left={date} right={baseDetails?.paymentMethod || "N/A"} />
      </>

      <Line />
    </>
  );
};

export const renderOrderHeader004 = (
  isB2cOrder,
  isDeliveryOrder,
  reprint,
  orderId,
  invoiceId,
  baseOrderResponse,
  baseDetails,
  date,
  returnId,
  printType,
  store004 = false
) => {
  return (
    <>
      <Text align="center" bold={true}>
        {printType === "credit-note" ? "Debit Note" : "Cash Bill"}
      </Text>

      <Line />

      <>
        <Row
          left={<Text>{`Customer Name: ${baseOrderResponse?.customerName || "N/A"}`}</Text>}
          right={baseDetails?.paymentMethod || "N/A"}
        />
      </>

      <Line />
      {printType == "credit-note" && (
        <Row left={returnId} right={baseOrderResponse?.orderId || "N/A"} />
      )}

      <Row
        left={`Bill No:${isB2cOrder || isDeliveryOrder ? orderId : invoiceId}`}
        right={` Date:${date.split(",")[0]}`}
      />

      <Line />
    </>
  );
};

export const renderOrderHeader005 = (
  isB2cOrder,
  isDeliveryOrder,
  reprint,
  orderId,
  invoiceId,
  baseOrderResponse,
  baseDetails,
  date,
  returnId,
  printType,
  store004 = false
) => {
  return (
    <>
      <Text>{`Customer Name: ${baseOrderResponse?.customerName || "N/A"}`}</Text>

      <Line />
      {printType == "credit-note" && (
        <Row left={returnId} right={baseOrderResponse?.orderId || "N/A"} />
      )}

      <Text>{`Bill No:${isB2cOrder || isDeliveryOrder ? orderId : invoiceId}`}</Text>
      <Text>{date}</Text>

      <Line />
    </>
  );
};

export const renderOrderHeader006 = (
  isB2cOrder,
  isDeliveryOrder,
  reprint,
  orderId,
  invoiceId,
  baseOrderResponse,
  baseDetails,
  date,
  returnId,
  imgBase64,
  printType,
  saleMode,
  branchSubType,
  tokenNumber
) => {
  const { firstName } = getFromLocalStorage("retailUserDetails");
  return (
    <>
      {reprint && (
        <Text align="center" bold={true}>{`(Reprinted by ${firstName})`}</Text>
      )}
      <Row
        left={`Date:${date.split(",")[0]}`}
        right={rowText(
          baseOrderResponse?.orderType === "RESTAURANT_ORDER"
            ? baseOrderResponse?.subOrderType
            : baseOrderResponse?.orderType
        )}
      />

      {(saleMode === "DINE_IN" && branchSubType === "FINE_DINE") ||
      !baseOrderResponse?.subOrderType ? (
        <>
          <Row left={`Time: ${date.split(",")[1]}`} right={`Cashier: ${firstName}`} />
          <Text>{`Bill No: ${invoiceId}`}</Text>
          <Text bold align="left">{`Table: ${baseOrderResponse?.tableNumber}`}</Text>
        </>
      ) : (
        <>
          <Row left={`Time: ${date.split(",")[1]}`} right={rowText(baseDetails?.paymentMethod ?? baseDetails?.returnMode)} />
          <Row left={`Cashier: ${firstName}`} right={`Bill No: ${baseOrderResponse?.orderId}`} />
        </>
      )}
      {baseOrderResponse?.customerName && (
        <Row
          left={`Customer: ${baseOrderResponse?.customerName}`}
          right={`Number: ${baseOrderResponse?.mobileNumber}`}
        />
      )}
      {tokenNumber && <Text bold align="left">{`KOT: ${tokenNumber}`}</Text>}

      <Line />
    </>
  );
};

export const renderOrderHeader007 = (
  isB2cOrder,
  isDeliveryOrder,
  reprint,
  orderId,
  invoiceId,
  baseOrderResponse,
  baseDetails,
  date,
  returnId,
  imgBase64,
  printType,
  saleMode,
  branchSubType,
  tokenNumber
) => {
  const { firstName } = getFromLocalStorage("retailUserDetails");
  return (
    <>
      {reprint && (
        <Text align="center" bold={true}>{`(Reprinted by ${firstName})`}</Text>
      )}
      <Row
        left={`Date:${date.split(",")[0]}`}
        right={rowText(
          baseOrderResponse?.orderType === "RESTAURANT_ORDER"
            ? baseOrderResponse?.subOrderType
            : baseOrderResponse?.orderType
        )}
      />

      {(saleMode === "DINE_IN" && branchSubType === "FINE_DINE") ||
      !baseOrderResponse?.subOrderType ? (
        <>
          <Row left={`Time: ${date.split(",")[1]}`} right={`Cashier: ${firstName}`} />
          <Text>{`Bill No: ${invoiceId}`}</Text>
          {baseOrderResponse?.customerName && (
            <Row
              left={`Customer: ${baseOrderResponse?.customerName}`}
              right={`Number: ${baseOrderResponse?.mobileNumber}`}
            />
          )}
          <Text bold align="left">{`Table: ${baseOrderResponse?.tableNumber}`}</Text>
        </>
      ) : (
        <>
          <Row left={`Time: ${date.split(",")[1]}`} right={rowText(baseDetails?.paymentMethod ?? baseDetails?.returnMode)} />
          <Row left={`Cashier: ${firstName}`} right={`Bill No: ${baseOrderResponse?.orderId}`} />
          {baseOrderResponse?.customerName && (
            <Row
              left={`Customer: ${baseOrderResponse?.customerName}`}
              right={`Number: ${baseOrderResponse?.mobileNumber}`}
            />
          )}
        </>
      )}
      {tokenNumber && <Text bold align="left">{`KOT: ${tokenNumber}`}</Text>}

      <Line />
    </>
  );
};

export const renderOrderItemsGst = (
  noGst,
  gst1slab,
  gst2slab,
  gst5slab,
  gst6slab,
  gst9slab,
  gst14slab,
  gst20slab,
  freeProducts
) => {
  return (
    <>
      <Text bold={true}>{"HSN   Particulars".padEnd(42)}</Text>
      <Text bold={true}>{"             Qty    MRP     Rate     Value"}</Text>
      <Line />
      {noGst?.length > 0 && renderSlabItems("No CGST + No SGST", noGst)}
      {gst1slab?.length > 0 && renderSlabItems("1.5% CGST + 1.5% SGST", gst1slab)}
      {gst2slab?.length > 0 && renderSlabItems("2.5% CGST + 2.5% SGST", gst2slab)}
      {gst5slab?.length > 0 && renderSlabItems("5% CGST + 5% SGST", gst5slab)}
      {gst6slab?.length > 0 && renderSlabItems("6% CGST + 6% SGST", gst6slab)}
      {gst9slab?.length > 0 && renderSlabItems("9% CGST + 9% SGST", gst9slab)}
      {gst14slab?.length > 0 && renderSlabItems("14% CGST + 14% SGST", gst14slab)}
      {gst20slab?.length > 0 && renderSlabItems("20% CGST + 20% SGST", gst20slab)}
      {freeProducts?.length > 0 && renderSlabItems("Free Products", freeProducts)}
      <Line />
    </>
  );
};

// template_001_02 variant: same slabs as renderOrderItemsGst, without the MRP and Rate columns
// and with a running serial number in place of the HSN code.
export const renderOrderItemsGst002_02 = (
  noGst,
  gst1slab,
  gst2slab,
  gst5slab,
  gst6slab,
  gst9slab,
  gst14slab,
  gst20slab,
  freeProducts
) => {
  const sno = createSnoCounter002_02();
  return (
    <>
      <Text bold={true}>{ITEM_HEADER_002_02}</Text>
      <Line />
      {noGst?.length > 0 && renderSlabItems002_02("No CGST + No SGST", noGst, sno)}
      {gst1slab?.length > 0 && renderSlabItems002_02("1.5% CGST + 1.5% SGST", gst1slab, sno)}
      {gst2slab?.length > 0 && renderSlabItems002_02("2.5% CGST + 2.5% SGST", gst2slab, sno)}
      {gst5slab?.length > 0 && renderSlabItems002_02("5% CGST + 5% SGST", gst5slab, sno)}
      {gst6slab?.length > 0 && renderSlabItems002_02("6% CGST + 6% SGST", gst6slab, sno)}
      {gst9slab?.length > 0 && renderSlabItems002_02("9% CGST + 9% SGST", gst9slab, sno)}
      {gst14slab?.length > 0 && renderSlabItems002_02("14% CGST + 14% SGST", gst14slab, sno)}
      {gst20slab?.length > 0 && renderSlabItems002_02("20% CGST + 20% SGST", gst20slab, sno)}
      {freeProducts?.length > 0 && renderSlabItems002_02("Free Products", freeProducts, sno)}
      <Line />
    </>
  );
};

export const renderOrderItemsGst004 = (noGst) => {
  return (
    <>
      <Text bold={true}>{"Description        MRP   Qty  Amount"}</Text>
      <Line />
      {noGst?.length && renderSlabItems004(noGst)}
      <Line />
    </>
  );
};

export const renderOrderItemsGst005 = (noGst) => {
  return (
    <>
      <Text bold={true}>{"Description   Rate   Qty  Total"}</Text>
      <Line />
      {noGst?.length && renderSlabItems005(noGst)}
      <Line />
    </>
  );
};

const splitSlabsByStatus = (slabs) => {
  const normal = {};
  const returned = {};
  const complementary = {};

  Object.entries(slabs).forEach(([key, items]) => {
    normal[key] = [];
    returned[key] = [];
    complementary[key] = [];

    items?.forEach((item) => {
      if (item?.status === "RETURNED") {
        returned[key].push(item);
      } else if (item?.inventoryStatus === "COMPLEMENTARY") {
        complementary[key].push(item);
      } else {
        normal[key].push(item);
      }
    });
  });

  return { normal, returned, complementary };
};

export const renderOrderItemsGst006 = (
  noGst,
  gst1slab,
  gst2slab,
  gst5slab,
  gst6slab,
  gst9slab,
  gst14slab,
  gst20slab,
  freeProducts
) => {
  const slabs = {
    noGst,
    gst1slab,
    gst2slab,
    gst5slab,
    gst6slab,
    gst9slab,
    gst14slab,
    gst20slab,
    freeProducts,
  };

  const { normal, returned, complementary } = splitSlabsByStatus(slabs);

  const hasReturnedItems = Object.values(returned).some((slab) => slab?.length > 0);
  const hasComplementaryItems = Object.values(complementary).some((slab) => slab?.length > 0);

  return (
    <>
      <Text bold={true}>{"Description       Price  Qty      Amount"}</Text>
      <Line />

      {normal?.noGst?.length > 0 && renderSlabItems006(normal?.noGst)}
      {normal?.gst1slab?.length > 0 && renderSlabItems006(normal?.gst1slab)}
      {normal?.gst2slab?.length > 0 && renderSlabItems006(normal?.gst2slab)}
      {normal?.gst5slab?.length > 0 && renderSlabItems006(normal?.gst5slab)}
      {normal?.gst6slab?.length > 0 && renderSlabItems006(normal?.gst6slab)}
      {normal?.gst9slab?.length > 0 && renderSlabItems006(normal?.gst9slab)}
      {normal?.gst14slab?.length > 0 && renderSlabItems006(normal?.gst14slab)}
      {normal?.gst20slab?.length > 0 && renderSlabItems006(normal?.gst20slab)}
      {normal?.freeProducts?.length > 0 && renderSlabItems006(normal?.freeProducts)}

      {hasReturnedItems && (
        <>
          <Line />
          <Text bold align="center">
            {"Returned Items"}
          </Text>
          <Line />

          {returned?.noGst?.length > 0 && renderSlabItems006(returned?.noGst)}
          {returned?.gst1slab?.length > 0 && renderSlabItems006(returned?.gst1slab)}
          {returned?.gst2slab?.length > 0 && renderSlabItems006(returned?.gst2slab)}
          {returned?.gst5slab?.length > 0 && renderSlabItems006(returned?.gst5slab)}
          {returned?.gst6slab?.length > 0 && renderSlabItems006(returned?.gst6slab)}
          {returned?.gst9slab?.length > 0 && renderSlabItems006(returned?.gst9slab)}
          {returned?.gst14slab?.length > 0 && renderSlabItems006(returned?.gst14slab)}
          {returned?.gst20slab?.length > 0 && renderSlabItems006(returned?.gst20slab)}
          {returned?.freeProducts?.length > 0 && renderSlabItems006(returned?.freeProducts)}
        </>
      )}

      {hasComplementaryItems && (
        <>
          <Line />
          <Text bold align="center">
            {"Complementary Items"}
          </Text>
          <Line />

          {complementary?.noGst?.length > 0 && renderSlabItems006(complementary?.noGst)}
          {complementary?.gst1slab?.length > 0 && renderSlabItems006(complementary?.gst1slab)}
          {complementary?.gst2slab?.length > 0 && renderSlabItems006(complementary?.gst2slab)}
          {complementary?.gst5slab?.length > 0 && renderSlabItems006(complementary?.gst5slab)}
          {complementary?.gst6slab?.length > 0 && renderSlabItems006(complementary?.gst6slab)}
          {complementary?.gst9slab?.length > 0 && renderSlabItems006(complementary?.gst9slab)}
          {complementary?.gst14slab?.length > 0 && renderSlabItems006(complementary?.gst14slab)}
          {complementary?.gst20slab?.length > 0 && renderSlabItems006(complementary?.gst20slab)}
          {complementary?.freeProducts?.length > 0 &&
            renderSlabItems006(complementary?.freeProducts)}
        </>
      )}

      <Line />
    </>
  );
};

// export const renderOrderItemsGst006 = (
//   noGst,
//   gst1slab,
//   gst2slab,
//   gst5slab,
//   gst6slab,
//   gst9slab,
//   gst14slab,
//   gst20slab,
//   freeProducts
// ) => {
//   const slabs = [
//     noGst,
//     gst1slab,
//     gst2slab,
//     gst5slab,
//     gst6slab,
//     gst9slab,
//     gst14slab,
//     gst20slab,
//     freeProducts,
//   ];

//   return (
//     <>
//       <Text bold={true}>{"Description       Price  Quantity  Amount"}</Text>
//       <Line />
//       {noGst?.length > 0 && renderSlabItems006(noGst)}
//       {gst1slab?.length > 0 && renderSlabItems006(gst1slab)}
//       {gst2slab?.length > 0 && renderSlabItems006(gst2slab)}
//       {gst5slab?.length > 0 && renderSlabItems006(gst5slab)}
//       {gst6slab?.length > 0 && renderSlabItems006(gst6slab)}
//       {gst9slab?.length > 0 && renderSlabItems006(gst9slab)}
//       {gst14slab?.length > 0 && renderSlabItems006(gst14slab)}
//       {gst20slab?.length > 0 && renderSlabItems006(gst20slab)}
//       {freeProducts?.length > 0 && renderSlabItems006(freeProducts)}
//       <Line />
//     </>
//   );
// };

export const renderOrderItemsGst007 = (
  noGst,
  gst1slab,
  gst2slab,
  gst5slab,
  gst6slab,
  gst9slab,
  gst14slab,
  gst20slab,
  freeProducts
) => {
  const slabs = {
    noGst,
    gst1slab,
    gst2slab,
    gst5slab,
    gst6slab,
    gst9slab,
    gst14slab,
    gst20slab,
    freeProducts,
  };
  const { normal, returned, complementary } = splitSlabsByStatus(slabs);
  const hasReturnedItems = Object.values(returned).some((slab) => slab?.length > 0);
  const hasComplementaryItems = Object.values(complementary).some((slab) => slab?.length > 0);
  return (
    <>
      <Text bold={true}>{"Description       Price  Qty      Amount"}</Text>
      <Line />

      {normal?.noGst?.length > 0 && renderSlabItems007(normal?.noGst)}
      {normal?.gst1slab?.length > 0 && renderSlabItems007(normal?.gst1slab)}
      {normal?.gst2slab?.length > 0 && renderSlabItems007(normal?.gst2slab)}
      {normal?.gst5slab?.length > 0 && renderSlabItems007(normal?.gst5slab)}
      {normal?.gst6slab?.length > 0 && renderSlabItems007(normal?.gst6slab)}
      {normal?.gst9slab?.length > 0 && renderSlabItems007(normal?.gst9slab)}
      {normal?.gst14slab?.length > 0 && renderSlabItems007(normal?.gst14slab)}
      {normal?.gst20slab?.length > 0 && renderSlabItems007(normal?.gst20slab)}
      {normal?.freeProducts?.length > 0 && renderSlabItems007(normal?.freeProducts)}

      {hasReturnedItems && (
        <>
          <Line />
          <Text bold align="center">
            {"Returned Items"}
          </Text>
          <Line />

          {returned?.noGst?.length > 0 && renderSlabItems007(returned?.noGst)}
          {returned?.gst1slab?.length > 0 && renderSlabItems007(returned?.gst1slab)}
          {returned?.gst2slab?.length > 0 && renderSlabItems007(returned?.gst2slab)}
          {returned?.gst5slab?.length > 0 && renderSlabItems007(returned?.gst5slab)}
          {returned?.gst6slab?.length > 0 && renderSlabItems007(returned?.gst6slab)}
          {returned?.gst9slab?.length > 0 && renderSlabItems007(returned?.gst9slab)}
          {returned?.gst14slab?.length > 0 && renderSlabItems007(returned?.gst14slab)}
          {returned?.gst20slab?.length > 0 && renderSlabItems007(returned?.gst20slab)}
          {returned?.freeProducts?.length > 0 && renderSlabItems007(returned?.freeProducts)}
        </>
      )}

      {hasComplementaryItems && (
        <>
          <Line />
          <Text bold align="center">
            {"Complementary Items"}
          </Text>
          <Line />

          {complementary?.noGst?.length > 0 && renderSlabItems007(complementary?.noGst)}
          {complementary?.gst1slab?.length > 0 && renderSlabItems007(complementary?.gst1slab)}
          {complementary?.gst2slab?.length > 0 && renderSlabItems007(complementary?.gst2slab)}
          {complementary?.gst5slab?.length > 0 && renderSlabItems007(complementary?.gst5slab)}
          {complementary?.gst6slab?.length > 0 && renderSlabItems007(complementary?.gst6slab)}
          {complementary?.gst9slab?.length > 0 && renderSlabItems007(complementary?.gst9slab)}
          {complementary?.gst14slab?.length > 0 && renderSlabItems007(complementary?.gst14slab)}
          {complementary?.gst20slab?.length > 0 && renderSlabItems007(complementary?.gst20slab)}
          {complementary?.freeProducts?.length > 0 &&
            renderSlabItems007(complementary?.freeProducts)}
        </>
      )}
      <Line />
    </>
  );
};

// export const renderOrderItemsGst007 = (
//   noGst,
//   gst1slab,
//   gst2slab,
//   gst5slab,
//   gst6slab,
//   gst9slab,
//   gst14slab,
//   gst20slab,
//   freeProducts
// ) => {
//   return (
//     <>
//       <Text bold={true}>{"Description       Price    Qty   Amount"}</Text>
//       <Line />
//       {noGst?.length > 0 && renderSlabItems007(noGst)}
//       {gst1slab?.length > 0 && renderSlabItems007(gst1slab)}
//       {gst2slab?.length > 0 && renderSlabItems007(gst2slab)}
//       {gst5slab?.length > 0 && renderSlabItems007(gst5slab)}
//       {gst6slab?.length > 0 && renderSlabItems007(gst6slab)}
//       {gst9slab?.length > 0 && renderSlabItems007(gst9slab)}
//       {gst14slab?.length > 0 && renderSlabItems007(gst14slab)}
//       {gst20slab?.length > 0 && renderSlabItems007(gst20slab)}
//       {freeProducts?.length > 0 && renderSlabItems007(freeProducts)}
//       <Line />
//     </>
//   );
// };

export const renderOrderItemsList = (orderItems, itemSno = false, useVariantName = false) => {
  return (
    <>
      <Text bold={true}>{`${itemSno ? "Sno" : "HSN"}   Particulars`.padEnd(42)}</Text>
      <Text bold={true}>{"             Qty    MRP     Rate     Value"}</Text>
      <Line />
      {orderItems?.length && renderSlabItems("", orderItems, itemSno, useVariantName)}
      <Line />
    </>
  );
};

export const renderOrderDetails = (data, template003) => {
  const { isB2cOrder, baseOrderResponse, totalQuantity, printType, baseDetails } = data;

  return (
    <>
      {printType === "credit-note" ? null : (
        <>
          {baseOrderResponse?.mobileNumber ? (
            <>
              {" "}
              <Row
                left={
                  <Text>{`Pts Earned: ${baseDetails?.earnedPoints || 0}(${
                    baseDetails?.earnedPointValue || 0
                  }Rs)`}</Text>
                }
                right={
                  <Text>{`Pts Used: ${baseDetails?.loyaltyPoints || 0}(${
                    baseDetails?.loyaltyPointsValue || 0
                  }Rs)`}</Text>
                }
              />
              <Row
                left={<Text>{`Coupons: ${baseDetails?.couponValue || 0}`}</Text>}
                right={<Text>{`Remanining Pts: ${baseDetails?.totalAvailablePoints || 0}`}</Text>}
              />
            </>
          ) : (
            <Text>{`Coupons: ${baseDetails?.couponValue || 0}`}</Text>
          )}
          {isB2cOrder && (
            <>
              <Text>{`Delivery Charges: ${baseDetails?.deliveryCharges || 0}`}</Text>
              <Text>{`Wallet: ${baseDetails?.redeemableWalletBalance || 0}`}</Text>
            </>
          )}
        </>
      )}

      {template003 ? (
        <>
          {printType === "credit-note" ? (
            <Row
              left={
                <Text bold={true}>{`Total Items: ${baseOrderResponse?.numberOfLineItems}`}</Text>
              }
              right={
                <Text
                  bold={true}
                >{`Returned Items: ${baseOrderResponse?.numberOfReturnedItems}`}</Text>
              }
            />
          ) : null}
          <Line />
          <Row
            left={
              <Text bold={true} size={{ width: 2, height: 2 }}>
                {printType === "credit-note" ? "Total Returned" : "Total Amount"}
              </Text>
            }
            right={
              <Text bold={true} size={{ width: 2, height: 2 }}>
                {printType === "credit-note"
                  ? baseDetails?.refund || 0
                  : baseDetails?.grandTotal || 0}
              </Text>
            }
          />
          <Line character="=" />
        </>
      ) : (
        <>
          {printType === "credit-note" ? (
            <>
              <Row
                left={
                  <Text bold={true}>{`Total Items: ${baseOrderResponse?.numberOfLineItems}`}</Text>
                }
                right={
                  <Text
                    bold={true}
                  >{`Returned Items: ${baseOrderResponse?.numberOfReturnedItems}`}</Text>
                }
              />
              <Line />
              <Row
                left={<Text bold={true}>{`Total Amount: ${baseOrderResponse?.amountPaid}`}</Text>}
                right={<Text bold={true}>{`Refund: ${baseOrderResponse?.refund}`}</Text>}
              />
              <Line />
              <Row
                left={<Text bold={true}>{`Refund Mode: ${baseDetails?.returnMode}`}</Text>}
                right={<Text bold={true}>{null}</Text>}
              />
              <Line />
            </>
          ) : (
            <>
              <Line />
              <Row
                left={
                  <Text bold={true}>{`Items: ${
                    baseOrderResponse?.orderItemList?.filter((item) => item?.voidStatus == null)
                      ?.length
                  }  Qty: ${totalQuantity}`}</Text>
                }
                right={<Text bold={true}>{`Total Amount: ${baseDetails?.grandTotal}`}</Text>}
              />
              <Line />
            </>
          )}
        </>
      )}
    </>
  );
};

export const renderOrderDetails004 = (data, template003) => {
  const { isB2cOrder, baseOrderResponse, totalQuantity, printType, baseDetails } = data;

  return (
    <>
      {printType === "credit-note" ? null : (
        <>
          {baseOrderResponse?.mobileNumber ? (
            <>
              {" "}
              <Row
                left={
                  <Text>{`Pts Earned: ${baseDetails?.earnedPoints || 0}(${
                    baseDetails?.earnedPointValue || 0
                  }Rs)`}</Text>
                }
                right={
                  <Text>{`Pts Used: ${baseDetails?.loyaltyPoints || 0}(${
                    baseDetails?.loyaltyPointsValue || 0
                  }Rs)`}</Text>
                }
              />
              <Row
                left={<Text>{`Coupons: ${baseDetails?.couponValue || 0}`}</Text>}
                right={<Text>{`Remanining Pts: ${baseDetails?.totalAvailablePoints || 0}`}</Text>}
              />
            </>
          ) : (
            <Text>{`Coupons: ${baseDetails?.couponValue || 0}`}</Text>
          )}
          {isB2cOrder && (
            <>
              <Text>{`Delivery Charges: ${baseDetails?.deliveryCharges || 0}`}</Text>
              <Text>{`Wallet: ${baseDetails?.redeemableWalletBalance || 0}`}</Text>
            </>
          )}
        </>
      )}

      {template003 ? (
        <>
          {printType === "credit-note" ? (
            <Row
              left={
                <Text bold={true}>{`Total Items: ${baseOrderResponse?.numberOfLineItems}`}</Text>
              }
              right={
                <Text
                  bold={true}
                >{`Returned Items: ${baseOrderResponse?.numberOfReturnedItems}`}</Text>
              }
            />
          ) : null}
          <Line />
          <Row
            left={
              <Text bold={true} size={{ width: 2, height: 2 }}>
                {printType === "credit-note" ? "Total Returned" : "Total Amount"}
              </Text>
            }
            right={
              <Text bold={true} size={{ width: 2, height: 2 }}>
                {printType === "credit-note"
                  ? baseDetails?.refund || 0
                  : baseDetails?.grandTotal || 0}
              </Text>
            }
          />
          <Line character="=" />
        </>
      ) : (
        <>
          {printType === "credit-note" ? (
            <>
              <Row
                left={
                  <Text bold={true}>{`Total Items: ${baseOrderResponse?.numberOfLineItems}`}</Text>
                }
                right={
                  <Text
                    bold={true}
                  >{`Returned Items: ${baseOrderResponse?.numberOfReturnedItems}`}</Text>
                }
              />
              <Line />
              <Row
                left={<Text bold={true}>{`Total Amount: ${baseOrderResponse?.amountPaid}`}</Text>}
                right={<Text bold={true}>{`Refund: ${baseOrderResponse?.refund}`}</Text>}
              />
              <Line />
              <Row
                left={<Text bold={true}>{`Refund Mode: ${baseDetails?.returnMode}`}</Text>}
                right={<Text bold={true}>{null}</Text>}
              />
              <Line />
            </>
          ) : (
            <>
              <Line />

              <Text bold={true}>
                {`Items: ${
                  baseOrderResponse?.orderItemList?.filter((item) => item?.voidStatus == null)
                    ?.length
                }  Qty: ${totalQuantity}`}
              </Text>

              <Line />
              <Text align="center">{`Net Amount: ${baseDetails?.grandTotal}`}</Text>
              <Line />
              <Text size={{ width: 2, height: 2 }} align="center" bold={true}>
                {`Total Amount: ${baseDetails?.grandTotal}`}
              </Text>
            </>
          )}
        </>
      )}
    </>
  );
};

export const renderOrderDetails005 = (data, template003) => {
  const { isB2cOrder, baseOrderResponse, totalQuantity, printType, baseDetails } = data;

  return (
    <>
      {printType === "credit-note" ? null : (
        <>
          {baseOrderResponse?.mobileNumber ? (
            <>
              {" "}
              <Row
                left={
                  <Text>{`Pts Earned: ${baseDetails?.earnedPoints || 0}(${
                    baseDetails?.earnedPointValue || 0
                  }Rs)`}</Text>
                }
                right={
                  <Text>{`Pts Used: ${baseDetails?.loyaltyPoints || 0}(${
                    baseDetails?.loyaltyPointsValue || 0
                  }Rs)`}</Text>
                }
              />
              <Row
                left={<Text>{`Coupons: ${baseDetails?.couponValue || 0}`}</Text>}
                right={<Text>{`Remanining Pts: ${baseDetails?.totalAvailablePoints || 0}`}</Text>}
              />
            </>
          ) : (
            <Text>{`Coupons: ${baseDetails?.couponValue || 0}`}</Text>
          )}
          {isB2cOrder && (
            <>
              <Text>{`Delivery Charges: ${baseDetails?.deliveryCharges || 0}`}</Text>
              <Text>{`Wallet: ${baseDetails?.redeemableWalletBalance || 0}`}</Text>
            </>
          )}
        </>
      )}

      {template003 ? (
        <>
          {printType === "credit-note" ? (
            <Row
              left={
                <Text bold={true}>{`Total Items: ${baseOrderResponse?.numberOfLineItems}`}</Text>
              }
              right={
                <Text
                  bold={true}
                >{`Returned Items: ${baseOrderResponse?.numberOfReturnedItems}`}</Text>
              }
            />
          ) : null}
          <Line />
          <Row
            left={
              <Text bold={true} size={{ width: 1, height: 1 }}>
                {printType === "credit-note" ? "Total Returned" : "Total Amount"}
              </Text>
            }
            right={
              <Text bold={true} size={{ width: 1, height: 1 }}>
                {printType === "credit-note"
                  ? baseDetails?.refund || 0
                  : baseDetails?.grandTotal || 0}
              </Text>
            }
          />
          <Line character="=" />
        </>
      ) : (
        <>
          {printType === "credit-note" ? (
            <>
              <Row
                left={
                  <Text bold={true}>{`Total Items: ${baseOrderResponse?.numberOfLineItems}`}</Text>
                }
                right={
                  <Text
                    bold={true}
                  >{`Returned Items: ${baseOrderResponse?.numberOfReturnedItems}`}</Text>
                }
              />
              <Line />
              <Row
                left={<Text bold={true}>{`Total Amount: ${baseOrderResponse?.amountPaid}`}</Text>}
                right={<Text bold={true}>{`Refund: ${baseOrderResponse?.refund}`}</Text>}
              />
              <Line />
              <Row
                left={<Text bold={true}>{`Refund Mode: ${baseDetails?.returnMode}`}</Text>}
                right={<Text bold={true}>{null}</Text>}
              />
              <Line />
            </>
          ) : (
            <>
              <Line />

              <Text bold={true}>{`Items: ${
                baseOrderResponse?.orderItemList?.filter((item) => item?.voidStatus == null)?.length
              }  Qty: ${totalQuantity}`}</Text>

              <Line />
              <Text align="center">{`Net Amount: ${baseDetails?.grandTotal}`}</Text>
              <Line />
              <Text size={{ width: 1.8, height: 1.8 }} align="center" bold={true}>
                {`Total Amount: ${baseDetails?.grandTotal}`}
              </Text>
            </>
          )}
        </>
      )}
    </>
  );
};

export const renderPaymentDetails = (baseOrderResponse, baseDetails, printType) => {
  return (
    <>
      {printType === "credit-note" ? null : (
        <>
          {Number(baseOrderResponse?.tender) && (
            <>
              <Row
                left={<Text bold={true}>{`Tender: ${baseOrderResponse?.tender}`}</Text>}
                right={<Text bold={true}>{`Change: ${baseOrderResponse?.balance}`}</Text>}
              />
              <Line />
            </>
          )}

          {localStorage.getItem("retailType") === "RESTAURANT" &&
            baseDetails?.deliveryCharges !== 0 && (
              <>
                <Text bold={true}>{`Delivery Charges: ${baseDetails?.deliveryCharges || 0}`}</Text>
                <Line />
              </>
            )}
          {localStorage.getItem("retailType") === "RESTAURANT" &&
            baseDetails?.packingCharges !== 0 && (
              <>
                <Text bold={true}>{`Packing Charges: ${baseDetails?.packingCharges || 0}`}</Text>
                <Line />
              </>
            )}

          {baseDetails?.payments?.length > 1 && (
            <>
              {baseDetails?.payments.map((e) => (
                <Text>{`${e.paymentMethod}: ${e.amountPaid}`}</Text>
              ))}
              <Line />
            </>
          )}
        </>
      )}
    </>
  );
};

export const renderPaymentDetails006 = (
  baseOrderResponse,
  baseDetails,
  printType,
  gstBreakupDetails,
  saleMode
) => {
  const totalQty =
    baseOrderResponse?.orderItemList?.reduce((sum, item) => {
      const returnedQty =
        item?.returnedQuantity !== null && item?.returnedQuantity !== undefined
          ? Math.abs(Number(item.returnedQuantity))
          : null;

      const qtyBySpecs =
        item?.quantityBySpecs !== null && item?.quantityBySpecs !== undefined
          ? Math.abs(Number(item.quantityBySpecs))
          : null;

      const qty =
        item?.quantity !== null && item?.quantity !== undefined
          ? Math.abs(Number(item.quantity))
          : 0;

      const qtyToUse = returnedQty ?? qtyBySpecs ?? qty;
      return sum + (isNaN(qtyToUse) ? 0 : qtyToUse);
    }, 0) || 0;

  // Check if loyalty or coupons have values
  const hasLoyalty = baseDetails?.loyaltyPoints && baseDetails?.loyaltyPoints !== 0;
  const hasCoupons = baseDetails?.couponValue && baseDetails?.couponValue !== 0;
  const appliedDiscount = Number(baseDetails?.appliedDiscount ?? 0);
  const hasDiscount = appliedDiscount > 0;
  const totalDiscount = Number(baseDetails?.totalDiscount ?? 0);
  const showLoyaltyCouponsRow = hasLoyalty || hasCoupons;

  // Format loyalty points display
  const loyaltyDisplay = `${baseDetails?.loyaltyPoints || 0}(${
    baseDetails?.loyaltyPointsValue || 0
  }Rs)`;

  // Discount is shown inline on the loyalty/coupon row only when exactly one of them is
  // present (right slot would otherwise be empty). When both are present, discount gets its
  // own row. When neither is present, discount also gets its own row.
  const discountInline = hasDiscount && !!hasLoyalty !== !!hasCoupons;

  // Determine left and right content
  let leftContent = <Text />;
  let rightContent = <Text />;

  if (hasLoyalty && hasCoupons) {
    leftContent = <Text>{`Loyalty Redeemed: ${loyaltyDisplay}`}</Text>;
    rightContent = <Text>{`Coupons: ${baseDetails?.couponValue}`}</Text>;
  } else if (hasLoyalty) {
    leftContent = <Text>{`Loyalty Redeemed: ${loyaltyDisplay}`}</Text>;
    rightContent = discountInline ? <Text>{`Discount: ${appliedDiscount}`}</Text> : <Text />;
  } else if (hasCoupons) {
    leftContent = <Text>{`Coupons: ${baseDetails?.couponValue}`}</Text>;
    rightContent = discountInline ? <Text>{`Discount: ${appliedDiscount}`}</Text> : <Text />;
  }

  // Check if payment split should be shown
  const showPaymentSplit =
    (saleMode === "TAKE_AWAY" || saleMode === "DELIVERY") &&
    baseDetails?.paymentMethod === "SPLIT" &&
    baseDetails?.payments?.length > 0;

  return (
    <>
      {(showLoyaltyCouponsRow || discountInline) && <Row left={leftContent} right={rightContent} />}

      {hasDiscount && !discountInline && (
        <Row left={<Text>{`Discount:`}</Text>} right={<Text>{appliedDiscount}</Text>} />
      )}

      {totalDiscount > 0 && (
        <Row left={<Text>{`Total Discount:`}</Text>} right={<Text>{totalDiscount}</Text>} />
      )}

      <Line />

      {/* Items and Total Quantity Row */}
      <Row
        left={
          <Text>{`Items: ${baseOrderResponse?.orderItemList?.length || 0} Qty: ${totalQty}`}</Text>
        }
        right={<Text>{`Total Amount: ${baseDetails?.grandTotal || 0}`}</Text>}
      />

      {/* GST Breakup */}
      {gstBreakupDetails?.map((e, i) => {
        if (e?.gstRate !== null && e?.gstRate !== 0) {
          const halfRate = (e?.gstRate / 2).toFixed(2);
          return (
            <React.Fragment key={i}>
              <Row
                left={<Text>{`CGST @${halfRate}%:`}</Text>}
                right={<Text>{e?.cgst || 0}</Text>}
              />
              <Row
                left={<Text>{`SGST @${halfRate}%:`}</Text>}
                right={<Text>{e?.sgst || 0}</Text>}
              />
            </React.Fragment>
          );
        }
        return null;
      })}

      <Line />

      {/* Payment Split Details - Show only for TAKE_AWAY/DELIVERY with SPLIT payment */}
      {showPaymentSplit && (
        <>
          {baseDetails.payments.map((payment, index) => (
            <Row
              key={index}
              left={<Text>{`${payment.paymentMethod}:`}</Text>}
              right={<Text>{payment.amountPaid}</Text>}
            />
          ))}
          <Line />
        </>
      )}

      {/* Additional Charges */}
      {printType !== "credit-note" && (
        <>
          {baseDetails?.deliveryCharges !== 0 && (
            <Row
              left={<Text>Delivery Charges:</Text>}
              right={<Text>{baseDetails?.deliveryCharges || 0}</Text>}
            />
          )}
          {baseDetails?.packingCharges !== 0 && (
            <Row
              left={<Text>Packing Charges:</Text>}
              right={<Text>{baseDetails?.packingCharges || 0}</Text>}
            />
          )}
        </>
      )}

      <Line type="double" />

      {/* Grand Total */}
      <Text bold align="center" size={{ width: 2, height: 2 }}>
        {`Grand Total: ${baseDetails?.grandTotal || 0}`}
      </Text>

      <Line type="double" />
    </>
  );
};

export const renderTaxBreakup = (gstBreakupDetails, cessItems) => {
  return (
    <>
      {!!gstBreakupDetails?.length && (
        <>
          <Text align="center" bold={true}>
            {"<------------ GST Breakup ------------->"}
          </Text>
          <Line />
          <Text bold={true}>{"SNo   TaxableAmt    CGST   SGST   TotalAmt"}</Text>
          <Line />
          {gstBreakupDetails
            ?.filter((item) => item.taxableAmt != null)
            ?.map((e, i) => (
              <Text>{`${i + 1}     ${e.taxableAmt
                .toFixed(2)
                .toString()
                .slice(0, 10)
                .padEnd(10)}  ${e.cgst.toFixed(2).toString().slice(0, 6).padStart(6)}${e.sgst
                .toFixed(2)
                .toString()
                .slice(0, 7)
                .padStart(7)}${
                e.totalAmt
                  ? e.totalAmt.toFixed(2).toString().slice(0, 11).padStart(11)
                  : "0.00".padStart(11)
              }`}</Text>
            ))}
          <Line />
        </>
      )}
      {!!cessItems?.length && (
        <>
          <Text align="center" bold={true}>
            {"<------------ CESS Breakup ------------>"}
          </Text>
          <Line />
          <Text bold={true}>{"SNo    TaxableAmt     CESS%       TotalAmt"}</Text>
          <Line />
          {cessItems?.map((e, i) => (
            <Text>{`${i + 1}      ${e.taxableAmt?.toFixed(2).toString().padEnd(10)}     ${e.cess
              ?.toString()
              .padStart(4)}%       ${e.totalAmt?.toFixed(2).toString().padStart(8)}`}</Text>
          ))}
          <Line />
        </>
      )}
    </>
  );
};

export const renderReturnedItemsDetails = (data, itemSno = false, useVariantName = false) => {
  return (
    <>
      <Text align="center" bold={true}>
        {"<-------- Not Available Items ------->"}
      </Text>
      <Line />
      <Text bold={true}>{`${itemSno ? "Sno" : "HSN"}   Particulars`.padEnd(42)}</Text>
      <Text bold={true}>{"             Qty    MRP     Rate     Value"}</Text>
      <Line />
      {data?.length > 0 && renderReturnedSlabItems("", data, false, useVariantName)}

      <Line />
    </>
  );
};

// template_001_02 variant: same as renderReturnedItemsDetails, without the MRP and Rate columns
// and with a running serial number in place of the HSN code.
export const renderReturnedItemsDetails002_02 = (data) => {
  return (
    <>
      <Text align="center" bold={true}>
        {"<-------- Not Available Items ------->"}
      </Text>
      <Line />
      <Text bold={true}>{ITEM_HEADER_002_02}</Text>
      <Line />
      {data?.length > 0 && renderReturnedSlabItems002_02("", data, createSnoCounter002_02())}

      <Line />
    </>
  );
};

export const renderReturnedItemsDetails004 = (data) => {
  return (
    <>
      <Text align="center" bold={true}>
        {"<--------- Not Available Items ------->"}
      </Text>
      <Line />
      <Text bold={true}>{"Description        MRP   Quantity  Amount"}</Text>
      <Line />
      {data?.length && renderReturnedSlabItems004(data)}
      <Line />
    </>
  );
};

export const renderFooter = (data, template003 = false) => {
  const {
    isB2cOrder,
    baseOrderResponse,
    deliveryAddress,
    cashDrawerOpen,
    printType,
    baseDetails,
    saleMode,
  } = data;

  const isInstantDelivery = baseOrderResponse?.deliveryType === "INSTANT";

  const formatTimeRange = (startTime, endTime) => {
    const options = {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };

    const start = new Date(startTime + "Z").toLocaleTimeString("en-IN", options);
    const end = new Date(endTime + "Z").toLocaleTimeString("en-IN", options);

    return `(${start} - ${end})`;
  };

  const isSlotDelivery = baseOrderResponse?.deliveryType === "SLOT";
  let slotTime = "";
  if (isSlotDelivery) {
    slotTime = formatTimeRange(baseOrderResponse?.startTime, baseOrderResponse?.endTime);
  }

  return (
    <>
      {printType !== "credit-note" && saleMode !== "DINE_IN" && (
        <>
          <Text align="center" bold={true}>
            Items sold cannot be returned or refunded
          </Text>
          <Line character="=" />

          {template003 ? (
            <Row
              left={<Text bold={true}>Total Savings</Text>}
              right={<Text bold={true}>{`${baseDetails.totalSavings || 0}`}</Text>}
            />
          ) : (
            <Row
              left={
                <Text bold={true} size={{ width: 2, height: 2 }}>
                  Total Savings
                </Text>
              }
              right={
                <Text bold={true} size={{ width: 2, height: 2 }}>{`${
                  baseDetails.totalSavings || 0
                }`}</Text>
              }
            />
          )}
        </>
      )}
      {printType === "credit-note" ? null : <Line character="=" />}
      {isB2cOrder && (
        <>
          <Text align="center" bold={true} size={{ width: 2, height: 2 }} underline="1dot-thick">
            {getOrderModeLabel(baseOrderResponse)}
            {isInstantDelivery && baseOrderResponse?.subOrderType !== "TAKE_AWAY" && "(INSTANT)"}
          </Text>
          <Text size={{ width: 1, height: 1 }}>{" "}</Text>
          {isSlotDelivery && (
            <Text align="center" size={{ width: 2, height: 2 }}>
              {slotTime}
            </Text>
          )}
          <Br />
          <Text>{`Customer Name: ${baseOrderResponse?.customerName || "N/A"}`}</Text>
          <Text>{`Mobile: ${baseOrderResponse?.mobileNumber || "N/A"}`}</Text>
          {deliveryAddress?.addressLine1 || deliveryAddress?.addressLine2 ? (
            <>
              {deliveryAddress?.addressLine1 ? (
                <>
                  <Text>{`Address: ${deliveryAddress?.addressLine1}, `}</Text>
                  <Text>
                    {`${deliveryAddress?.addressLine2 || ""}, ${deliveryAddress?.city || ""}, ${
                      deliveryAddress?.state || ""
                    }`}
                  </Text>
                </>
              ) : (
                <>
                  <Text>{`Address: ${deliveryAddress?.addressLine2}, `}</Text>
                  <Text>
                    {`${deliveryAddress?.city || ""}, ${deliveryAddress?.state || ""}`}
                  </Text>
                </>
              )}
            </>
          ) : (
            <Text>{`Address: N/A`}</Text>
          )}
          <Line />
          {!!baseOrderResponse?.shipmentId && (
            <QRCode align="center" height={150} content={baseOrderResponse?.shipmentId} />
          )}
        </>
      )}
      {localStorage.getItem("retailType") === "RESTAURANT" && (
        <>
          {deliveryAddress && (
            <>
              <Text bold={true} size={{ width: 1, height: 1 }}>
                Delivery Address:
              </Text>
              <Text>{`Customer Name: ${baseOrderResponse?.customerName || "N/A"}`}</Text>
              <Text>{`Mobile: ${baseOrderResponse?.mobileNumber || "N/A"}`}</Text>
              <Text>{`Address Type: ${deliveryAddress?.addressType || "N/A"}`}</Text>
              <Text>{`Address: ${
                deliveryAddress?.addressLine1
                  ? `${deliveryAddress?.addressLine1}, ${deliveryAddress?.addressLine2 || ""}, ${
                      deliveryAddress?.city
                    }`
                  : deliveryAddress?.addressLine2
                  ? `${deliveryAddress?.addressLine2}, ${deliveryAddress?.city || ""}`
                  : "N/A"
              }`}</Text>
              <Text>{`Pincode: ${deliveryAddress?.pinCode || "N/A"}`}</Text>
              <Line />
            </>
          )}
          {/* <Text bold={true}>{deliveryAddress}</Text>
          <Line /> */}
        </>
      )}
      {!isB2cOrder && saleMode !== "DINE_IN" && (
        <>
          <Text align="center" bold={true}>
            Please bring your own shopping bags and
          </Text>
          <Text align="center" bold={true}>
            Help Save Environment
          </Text>
          <Image align="center" src={recycleLogo} transforms={[transforms.floydSteinberg]} />
          <Line />
        </>
      )}
      {printType === "credit-note" ? null : (
        <>
          {!!baseDetails?.dynamicCouponDetails?.print && (
            <>
              <Text align="center">Special Offer only for you!</Text>
              <Text size={{ width: 2, height: 2 }} bold align="center">
                {baseDetails?.dynamicCouponDetails?.offerName}
              </Text>
              <Text align="center" bold>
                {baseDetails?.dynamicCouponDetails?.on || "N/A"}
              </Text>
              <Text align="center">on your next purchase.</Text>
              <Text align="center">Use below code to avail discount.</Text>
              <Text align="center" bold>
                Valid for {baseDetails?.dynamicCouponDetails?.validDays || "N/A"} days
              </Text>
              <Text align="center">
                Expires on {baseDetails?.dynamicCouponDetails?.expiryDate || "N/A"}
              </Text>
              <Text size={{ width: 2, height: 2 }} bold={true} align="center">
                {baseDetails?.dynamicCouponDetails?.code || "N/A"}
              </Text>
              {!!baseDetails?.dynamicCouponDetails?.code && (
                <Barcode
                  align="center"
                  type="CODE128"
                  content={baseDetails?.dynamicCouponDetails?.code}
                  height={80}
                />
              )}
              <Line />
            </>
          )}
        </>
      )}

      <Text align="center">Thank you for shopping with us!</Text>
      <Text align="center">@Powered by Pallet</Text>
      {cashDrawerOpen && <Cashdraw pin="2pin" />}
      <Cut />
    </>
  );
};

export const renderFooter001_01 = (data, template003 = false) => {
  const {
    isB2cOrder,
    baseOrderResponse,
    deliveryAddress,
    cashDrawerOpen,
    printType,
    baseDetails,
    saleMode,
  } = data;

  const isInstantDelivery = baseOrderResponse?.deliveryType === "INSTANT";

  return (
    <>
      {printType !== "credit-note" && saleMode !== "DINE_IN" && (
        <>
          {/* <Text align="center" bold={true}>
            Items sold cannot be returned or refunded
          </Text> */}
          {/* <Line character="=" /> */}

          {/* {template003 ? (
            <Row
              left={<Text bold={true}>Total Savings</Text>}
              right={<Text bold={true}>{`${baseDetails.totalSavings || 0}`}</Text>}
            />
          ) : (
            <Row
              left={
                <Text bold={true} size={{ width: 2, height: 2 }}>
                  Total Savings
                </Text>
              }
              right={
                <Text bold={true} size={{ width: 2, height: 2 }}>{`${
                  baseDetails.totalSavings || 0
                }`}</Text>
              }
            />
          )} */}
        </>
      )}
      {printType === "credit-note" ? null : <Line character="=" />}
      {isB2cOrder && (
        <>
          <Text align="center" bold={true} size={{ width: 2, height: 2 }} underline="1dot-thick">
            {getOrderModeLabel(baseOrderResponse)}
            {isInstantDelivery && baseOrderResponse?.subOrderType !== "TAKE_AWAY" && "(INSTANT)"}
          </Text>
          <Br />
          <Text>{`Customer Name: ${baseOrderResponse?.customerName || "N/A"}`}</Text>
          <Text>{`Mobile: ${baseOrderResponse?.mobileNumber || "N/A"}`}</Text>
          {deliveryAddress?.addressLine1 || deliveryAddress?.addressLine2 ? (
            <>
              {deliveryAddress?.addressLine1 ? (
                <>
                  <Text>{`Address: ${deliveryAddress?.addressLine1}, `}</Text>
                  <Text>
                    {`${deliveryAddress?.addressLine2 || ""}, ${deliveryAddress?.city || ""}, ${
                      deliveryAddress?.state || ""
                    }`}
                  </Text>
                </>
              ) : (
                <>
                  <Text>{`Address: ${deliveryAddress?.addressLine2}, `}</Text>
                  <Text>
                    {`${deliveryAddress?.city || ""}, ${deliveryAddress?.state || ""}`}
                  </Text>
                </>
              )}
            </>
          ) : (
            <Text>{`Address: N/A`}</Text>
          )}
          <Line />
          {!!baseOrderResponse?.shipmentId && (
            <QRCode align="center" height={150} content={baseOrderResponse?.shipmentId} />
          )}
        </>
      )}
      {localStorage.getItem("retailType") === "RESTAURANT" && (
        <>
          {deliveryAddress && (
            <>
              <Text bold={true} size={{ width: 1, height: 1 }}>
                Delivery Address:
              </Text>
              <Text>{`Customer Name: ${baseOrderResponse?.customerName || "N/A"}`}</Text>
              <Text>{`Mobile: ${baseOrderResponse?.mobileNumber || "N/A"}`}</Text>
              <Text>{`Address Type: ${deliveryAddress?.addressType || "N/A"}`}</Text>
              <Text>{`Address: ${
                deliveryAddress?.addressLine1
                  ? `${deliveryAddress?.addressLine1}, ${deliveryAddress?.addressLine2 || ""}, ${
                      deliveryAddress?.city
                    }`
                  : deliveryAddress?.addressLine2
                  ? `${deliveryAddress?.addressLine2}, ${deliveryAddress?.city || ""}`
                  : "N/A"
              }`}</Text>
              <Text>{`Pincode: ${deliveryAddress?.pinCode || "N/A"}`}</Text>
              <Line />
            </>
          )}
          {/* <Text bold={true}>{deliveryAddress}</Text>
          <Line /> */}
        </>
      )}
      {!isB2cOrder && saleMode !== "DINE_IN" && (
        <>
          <Text align="center" bold={true}>
            Please bring your own shopping bags and
          </Text>
          <Text align="center" bold={true}>
            Help Save Environment
          </Text>
          <Image align="center" src={recycleLogo} transforms={[transforms.floydSteinberg]} />
          <Line />
        </>
      )}
      {printType === "credit-note" ? null : (
        <>
          {!!baseDetails?.dynamicCouponDetails?.print && (
            <>
              <Text align="center">Special Offer only for you!</Text>
              <Text size={{ width: 2, height: 2 }} bold align="center">
                {baseDetails?.dynamicCouponDetails?.offerName}
              </Text>
              <Text align="center" bold>
                {baseDetails?.dynamicCouponDetails?.on || "N/A"}
              </Text>
              <Text align="center">on your next purchase.</Text>
              <Text align="center">Use below code to avail discount.</Text>
              <Text align="center" bold>
                Valid for {baseDetails?.dynamicCouponDetails?.validDays || "N/A"} days
              </Text>
              <Text align="center">
                Expires on {baseDetails?.dynamicCouponDetails?.expiryDate || "N/A"}
              </Text>
              <Text size={{ width: 2, height: 2 }} bold={true} align="center">
                {baseDetails?.dynamicCouponDetails?.code || "N/A"}
              </Text>
              {!!baseDetails?.dynamicCouponDetails?.code && (
                <Barcode
                  align="center"
                  type="CODE128"
                  content={baseDetails?.dynamicCouponDetails?.code}
                  height={80}
                />
              )}
              <Line />
            </>
          )}
        </>
      )}

      <Text align="center">Thank you for shopping with us!</Text>
      <Text align="center">@Powered by Pallet</Text>
      {cashDrawerOpen && <Cashdraw pin="2pin" />}
      <Cut />
    </>
  );
};

export const renderFooter004 = (data, template003 = false) => {
  const { isB2cOrder, baseOrderResponse, deliveryAddress, cashDrawerOpen, printType, baseDetails } =
    data;

  const isInstantDelivery = baseOrderResponse?.deliveryType === "INSTANT";

  return (
    <>
      {printType === "credit-note" ? null : <Line character="=" />}
      {isB2cOrder && (
        <>
          <Text align="center" bold={true} size={{ width: 2, height: 2 }} underline="1dot-thick">
            {getOrderModeLabel(baseOrderResponse)}
            {isInstantDelivery && baseOrderResponse?.subOrderType !== "TAKE_AWAY" && "(INSTANT)"}
          </Text>
          <Br />
          <Text>{`Customer Name: ${baseOrderResponse?.customerName || "N/A"}`}</Text>
          <Text>{`Mobile: ${baseOrderResponse?.mobileNumber || "N/A"}`}</Text>
          {deliveryAddress?.addressLine1 || deliveryAddress?.addressLine2 ? (
            <>
              {deliveryAddress?.addressLine1 ? (
                <>
                  <Text>{`Address: ${deliveryAddress?.addressLine1}, `}</Text>
                  <Text>
                    {`${deliveryAddress?.addressLine2 || ""}, ${deliveryAddress?.city || ""}, ${
                      deliveryAddress?.state || ""
                    }`}
                  </Text>
                </>
              ) : (
                <>
                  <Text>{`Address: ${deliveryAddress?.addressLine2}, `}</Text>
                  <Text>
                    {`${deliveryAddress?.city || ""}, ${deliveryAddress?.state || ""}`}
                  </Text>
                </>
              )}
            </>
          ) : (
            <Text>{`Address: N/A`}</Text>
          )}
          <Line />
          {!!baseOrderResponse?.shipmentId && (
            <QRCode align="center" height={150} content={baseOrderResponse?.shipmentId} />
          )}
        </>
      )}

      {printType === "credit-note" ? null : (
        <>
          {!!baseDetails?.dynamicCouponDetails?.print && (
            <>
              <Text align="center">Special Offer only for you!</Text>
              <Text size={{ width: 2, height: 2 }} bold align="center">
                {baseDetails?.dynamicCouponDetails?.offerName}
              </Text>
              <Text align="center" bold>
                {baseDetails?.dynamicCouponDetails?.on || "N/A"}
              </Text>
              <Text align="center">on your next purchase.</Text>
              <Text align="center">Use below code to avail discount.</Text>
              <Text align="center" bold>
                Valid for {baseDetails?.dynamicCouponDetails?.validDays || "N/A"} days
              </Text>
              <Text align="center">
                Expires on {baseDetails?.dynamicCouponDetails?.expiryDate || "N/A"}
              </Text>
              <Text size={{ width: 2, height: 2 }} bold={true} align="center">
                {baseDetails?.dynamicCouponDetails?.code || "N/A"}
              </Text>
              {!!baseDetails?.dynamicCouponDetails?.code && (
                <Barcode
                  align="center"
                  type="CODE128"
                  content={baseDetails?.dynamicCouponDetails?.code}
                  height={80}
                />
              )}
              <Line />
            </>
          )}
        </>
      )}

      <Text align="center">Thank you for shopping with us!</Text>
      <Text align="center">@Powered by Pallet</Text>
      <Text>{"\n\n\n"}</Text>
      {cashDrawerOpen && <Cashdraw pin="2pin" />}
      <Cut />
    </>
  );
};

export const renderFooter006 = (data, template003 = false) => {
  const { isB2cOrder, baseOrderResponse, deliveryAddress, cashDrawerOpen, printType, baseDetails } =
    data;

  const isInstantDelivery = baseOrderResponse?.deliveryType === "INSTANT";

  // 🔥 Helper to split long text into multiple lines
  const splitText = (text = "", maxLength = 32) => {
    const words = text.split(" ");
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
      if ((currentLine + word).length > maxLength) {
        lines.push(currentLine.trim());
        currentLine = word + " ";
      } else {
        currentLine += word + " ";
      }
    });

    if (currentLine) lines.push(currentLine.trim());

    return lines;
  };

  // 🔥 Build full address string
  const addressLines = deliveryAddress?.addressLine1 || deliveryAddress?.addressLine2
    ? splitText(
        deliveryAddress?.addressLine1
          ? `${deliveryAddress?.addressLine1}, ${deliveryAddress?.addressLine2 || ""}, ${deliveryAddress?.city || ""}, ${deliveryAddress?.state || ""}`
          : `${deliveryAddress?.addressLine2}, ${deliveryAddress?.city || ""}, ${deliveryAddress?.state || ""}`,
        32
      )
    : [];

  return (
    <>
      {printType === "credit-note" ? null : <Line character="=" />}

      {isB2cOrder && (
        <>
          <Text align="center" bold={true} size={{ width: 2, height: 2 }} underline="1dot-thick">
            {getOrderModeLabel(baseOrderResponse)}
            {isInstantDelivery && baseOrderResponse?.subOrderType !== "TAKE_AWAY" && "(INSTANT)"}
          </Text>

          <Br />

          <Text>{`Customer Name: ${baseOrderResponse?.customerName || "N/A"}`}</Text>

          <Text>{`Mobile: ${deliveryAddress?.phoneNo || baseOrderResponse?.mobileNumber || "N/A"}`}</Text>

          {deliveryAddress?.addressLine1 || deliveryAddress?.addressLine2 ? (
            <>
              <Text>{`Address:`}</Text>
              {addressLines.map((line, index) => (
                <Text key={index}>{line}</Text>
              ))}
              <Text>{`Pincode: ${deliveryAddress?.pinCode || ""}`}</Text>
            </>
          ) : (
            <Text>{`Address: N/A`}</Text>
          )}

          <Line />

          {!!baseOrderResponse?.shipmentId && (
            <QRCode align="center" height={150} content={baseOrderResponse?.shipmentId} />
          )}
        </>
      )}

      {printType === "credit-note" ? null : (
        <>
          {!!baseDetails?.dynamicCouponDetails?.print && (
            <>
              <Text align="center">Special Offer only for you!</Text>

              <Text size={{ width: 2, height: 2 }} bold align="center">
                {baseDetails?.dynamicCouponDetails?.offerName}
              </Text>

              <Text align="center" bold>
                {baseDetails?.dynamicCouponDetails?.on || "N/A"}
              </Text>

              <Text align="center">on your next purchase.</Text>
              <Text align="center">Use below code to avail discount.</Text>

              <Text align="center" bold>
                Valid for {baseDetails?.dynamicCouponDetails?.validDays || "N/A"} days
              </Text>

              <Text align="center">
                Expires on {baseDetails?.dynamicCouponDetails?.expiryDate || "N/A"}
              </Text>

              <Text size={{ width: 2, height: 2 }} bold={true} align="center">
                {baseDetails?.dynamicCouponDetails?.code || "N/A"}
              </Text>

              {!!baseDetails?.dynamicCouponDetails?.code && (
                <Barcode
                  align="center"
                  type="CODE128"
                  content={baseDetails?.dynamicCouponDetails?.code}
                  height={80}
                />
              )}

              <Line />
            </>
          )}
        </>
      )}

      <Text align="center">See you soon!</Text>
      <Text align="center">@Powered by Pallet</Text>

      {cashDrawerOpen && <Cashdraw pin="2pin" />}
      <Cut />
    </>
  );
};

export const renderFooter007 = (data, template003 = false) => {
  const { isB2cOrder, baseOrderResponse, deliveryAddress, cashDrawerOpen, printType, baseDetails } =
    data;

  const isInstantDelivery = baseOrderResponse?.deliveryType === "INSTANT";

  // 🔥 Helper to split long text
  const splitText = (text = "", maxLength = 32) => {
    const words = text.split(" ");
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
      if ((currentLine + word).length > maxLength) {
        lines.push(currentLine.trim());
        currentLine = word + " ";
      } else {
        currentLine += word + " ";
      }
    });

    if (currentLine) lines.push(currentLine.trim());

    return lines;
  };

  // 🔥 Build full address
  const addressLines = deliveryAddress?.addressLine1 || deliveryAddress?.addressLine2
    ? splitText(
        deliveryAddress?.addressLine1
          ? `${deliveryAddress?.addressLine1}, ${deliveryAddress?.addressLine2 || ""}, ${deliveryAddress?.city || ""}, ${deliveryAddress?.state || ""}`
          : `${deliveryAddress?.addressLine2}, ${deliveryAddress?.city || ""}, ${deliveryAddress?.state || ""}`,
        32
      )
    : [];

  return (
    <>
      {printType === "credit-note" ? null : <Line character="=" />}

      {isB2cOrder && (
        <>
          <Text align="center" bold={true} size={{ width: 2, height: 2 }} underline="1dot-thick">
            {getOrderModeLabel(baseOrderResponse)}
            {isInstantDelivery && baseOrderResponse?.subOrderType !== "TAKE_AWAY" && "(INSTANT)"}
          </Text>

          <Br />

          <Text>{`Customer Name: ${baseOrderResponse?.customerName || "N/A"}`}</Text>

          <Text>{`Mobile: ${deliveryAddress?.phoneNo || baseOrderResponse?.mobileNumber || "N/A"}`}</Text>

          {deliveryAddress?.addressLine1 || deliveryAddress?.addressLine2 ? (
            <>
              <Text>{`Address:`}</Text>
              {addressLines?.map((line, index) => (
                <Text key={index}>{line}</Text>
              ))}
              <Text>{`Pincode: ${deliveryAddress?.pinCode || ""}`}</Text>
            </>
          ) : (
            <Text>{`Address: N/A`}</Text>
          )}

          <Line />

          {!!baseOrderResponse?.shipmentId && (
            <QRCode align="center" height={150} content={baseOrderResponse?.shipmentId} />
          )}
        </>
      )}

      {printType === "credit-note" ? null : (
        <>
          {!!baseDetails?.dynamicCouponDetails?.print && (
            <>
              <Text align="center">Special Offer only for you!</Text>

              <Text size={{ width: 2, height: 2 }} bold align="center">
                {baseDetails?.dynamicCouponDetails?.offerName}
              </Text>

              <Text align="center" bold>
                {baseDetails?.dynamicCouponDetails?.on || "N/A"}
              </Text>

              <Text align="center">on your next purchase.</Text>
              <Text align="center">Use below code to avail discount.</Text>

              <Text align="center" bold>
                Valid for {baseDetails?.dynamicCouponDetails?.validDays || "N/A"} days
              </Text>

              <Text align="center">
                Expires on {baseDetails?.dynamicCouponDetails?.expiryDate || "N/A"}
              </Text>

              <Text size={{ width: 2, height: 2 }} bold={true} align="center">
                {baseDetails?.dynamicCouponDetails?.code || "N/A"}
              </Text>

              {!!baseDetails?.dynamicCouponDetails?.code && (
                <Barcode
                  align="center"
                  type="CODE128"
                  content={baseDetails?.dynamicCouponDetails?.code}
                  height={80}
                />
              )}

              <Line />
            </>
          )}
        </>
      )}

      <Text align="center">We hope you enjoyed! Do leave us a google review.</Text>

      <Text>{"\n"}</Text>

      <Text align="center">Stay updated by following us on instagram:</Text>

      <Text align="center">@originalbombaykulfi</Text>

      <Text>{"\n"}</Text>

      <Text align="center">40+ Outlets in India & Singapore</Text>

      {cashDrawerOpen && <Cashdraw pin="2pin" />}
      <Cut />
    </>
  );
};

export const renderToken007 = (
  tableId,
  tokenNumber,
  tableNumber,
  subOrderType,
  customerName,
  orderItems,
  saleMode,
  comments,
  layoutName,
  startTime,
  endTime,
  slotId
) => {
  // Word-wrap helper function
  const wrapTextByWords = (text, width) => {
    const words = text.split(" ");
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
      if ((currentLine + (currentLine ? " " : "") + word).length <= width) {
        currentLine += (currentLine ? " " : "") + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });

    if (currentLine) lines.push(currentLine);
    return lines;
  };

  const sanitizeForPrinter = (text = "") => {
    return text
      .replace(/[–—−]/g, "-")
      .replace(/[""]/g, '"')
      .replace(/['']/g, "'")
      .replace(/[™©®]/g, "")
      .replace(/[^\x00-\x7F]/g, "");
  };

  // Filter out returned items
  const activeOrderItems = orderItems?.filter((item) => item?.productStatus !== "RETURNED") || [];

  // Group items by prep station
  const itemsByPrepStation = activeOrderItems.reduce((acc, item) => {
    const prepStationId = item?.prepStationName ? item?.prepStationId : "no-station";
    const prepStationName = item?.prepStationName || null;

    if (!acc[prepStationId]) {
      acc[prepStationId] = {
        name: prepStationName,
        items: [],
      };
    }

    acc[prepStationId].items.push(item);
    return acc;
  }, {});

  const prepStations = Object.values(itemsByPrepStation);

  return (
    <>
      {prepStations.map((station, stationIndex) => (
        <React.Fragment key={`station-${stationIndex}`}>
          {/* Blank lines */}
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>

          {/* Layout name */}
          {saleMode === "DINE_IN" && layoutName && (
            <>
              <Text align="center" bold size={{ width: 2, height: 2 }}>
                {layoutName}
              </Text>
              <Line />
            </>
          )}

          {/* Token header */}
          <Text align="center" bold size={{ width: 2, height: 2 }}>
            {`KOT: ${tokenNumber} - ${tableNumber || "NA"}`}
          </Text>
          <Line />

          {/* Slot Info */}
          {slotId?.startsWith?.("IN") ? (
            <Text>Slot: Instant Delivery</Text>
          ) : startTime && endTime && formatSlot(startTime, endTime) ? (
            <Text>{formatSlot(startTime, endTime)}</Text>
          ) : null}

          {/* Date & Time */}
          <Row
            left={<Text>{`Date: ${new Date().toLocaleDateString()}`}</Text>}
            right={<Text>{`Time: ${new Date().toLocaleTimeString()}`}</Text>}
          />

          {/* Service info */}
          <Text>{`Service: ${subOrderType || "NA"}`}</Text>
          <Line />

          {/* Prep Station */}
          {station.name && (
            <>
              <Text align="center" bold size={{ width: 1.5, height: 1.5 }}>
                Prep Station: {station.name}
              </Text>
              <Line />
            </>
          )}

          {/* Items header */}
          <Row left={<Text bold>Items:</Text>} right={<Text bold>Qty</Text>} />
          <Line />

          {/* Items */}
          {station.items.map((e, i) => {
            const descWidth = 30;
            const productName = sanitizeForPrinter(e?.productName || "");
            const productLines = wrapTextByWords(productName, descWidth);

            return (
              <React.Fragment key={`item-${i}`}>
                {/* Product */}
                {productLines.map((line, idx) => (
                  <Row
                    key={`product-${i}-${idx}`}
                    left={<Text>{line}</Text>}
                    right={
                      idx === 0 ? (
                        <Text bold style={{ alignSelf: "flex-start" }}>
                          {e?.quantity}
                        </Text>
                      ) : (
                        <Text> </Text>
                      )
                    }
                  />
                ))}

                {/* Variant */}
                {e?.variantName && (
                  <>
                    {wrapTextByWords(sanitizeForPrinter(`  VAR: ${e.variantName}`), descWidth).map(
                      (line, idx) => (
                        <Text key={`variant-${i}-${idx}`}>{line}</Text>
                      )
                    )}
                  </>
                )}

                {/* Add-ons */}
                {e?.addonProduct?.length > 0 && (
                  <>
                    {wrapTextByWords(
                      sanitizeForPrinter(
                        `  ADD-ON: ${e.addonProduct
                          .map((a) => `${a?.itemName} x${a?.quantity || a?.qty || 1}`)
                          .join(", ")}`
                      ),
                      descWidth
                    ).map((line, idx) => (
                      <Text key={`addon-${i}-${idx}`}>{line}</Text>
                    ))}
                  </>
                )}

                {/* Item-level comments when global comments are null */}
                {e?.comments && (
                  <>
                    {wrapTextByWords(
                      sanitizeForPrinter(`  COMMENTS: ${e.comments}`),
                      descWidth
                    ).map((line, idx) => (
                      <Text key={`item-comment-${i}-${idx}`}>{line}</Text>
                    ))}
                  </>
                )}

                <Text> </Text>
              </React.Fragment>
            );
          })}

          <Line />

          {/* Global comments */}
          {comments && (
            <>
              {wrapTextByWords(sanitizeForPrinter(`Comments: ${comments}`), 38).map((line, idx) => (
                <Text key={`comment-${idx}`}>{line}</Text>
              ))}
              <Line />
            </>
          )}

          <Cut type="partial" line={0} />
        </React.Fragment>
      ))}
    </>
  );
};

export const renderToken006 = (
  tableId,
  tokenNumber,
  tableNumber,
  subOrderType,
  customerName,
  orderItems,
  saleMode,
  comments,
  layoutName,
  startTime,
  endTime,
  slotId
) => {
  const wrapTextByWords = (text, width) => {
    const words = text.split(" ");
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
      if ((currentLine + (currentLine ? " " : "") + word).length <= width) {
        currentLine += (currentLine ? " " : "") + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });

    if (currentLine) lines.push(currentLine);
    return lines;
  };

  const sanitizeForPrinter = (text = "") => {
    return text
      .replace(/[–—−]/g, "-")
      .replace(/[""]/g, '"')
      .replace(/['']/g, "'")
      .replace(/[™©®]/g, "")
      .replace(/[^\x00-\x7F]/g, "");
  };

  // Filter out returned items
  const activeOrderItems = orderItems?.filter((item) => item?.productStatus !== "RETURNED") || [];

  // Group items by prep station
  const itemsByPrepStation = activeOrderItems.reduce((acc, item) => {
    // If prepStationName is null, group all together as "no-station"
    const prepStationId = item?.prepStationName ? item?.prepStationId : "no-station";
    const prepStationName = item?.prepStationName || null;

    if (!acc[prepStationId]) {
      acc[prepStationId] = {
        name: prepStationName,
        items: [],
      };
    }

    acc[prepStationId].items.push(item);
    return acc;
  }, {});

  const prepStations = Object.values(itemsByPrepStation);

  return (
    <>
      {prepStations.map((station, stationIndex) => (
        <React.Fragment key={`station-${stationIndex}`}>
          {/* Layout name at top (for DINE_IN) */}
          {saleMode === "DINE_IN" && layoutName && (
            <>
              <Text align="center" bold size={{ width: 2, height: 2 }}>
                {layoutName}
              </Text>
              <Line />
            </>
          )}

          {/* Token header */}
          <Text align="center" bold size={{ width: 2, height: 2 }}>
            {`KOT: ${tokenNumber} - ${tableNumber || "NA"}`}
          </Text>
          <Line />

          {/* Slot Info */}
          {slotId?.startsWith?.("IN") ? (
            <Text>Slot: Instant Delivery</Text>
          ) : startTime && endTime && formatSlot(startTime, endTime) ? (
            <Text>{formatSlot(startTime, endTime)}</Text>
          ) : null}

          {/* Date & Time */}
          <Row
            left={<Text>{`Date: ${new Date().toLocaleDateString()}`}</Text>}
            right={<Text>{`Time: ${new Date().toLocaleTimeString()}`}</Text>}
          />

          {/* Service info */}
          <Text>{`Service: ${subOrderType || "NA"}`}</Text>
          <Line />

          {/* Prep Station Name Header - only if not null */}
          {station.name && (
            <>
              <Text align="center" bold size={{ width: 1.5, height: 1.5 }}>
                Prep Station: {station.name}
              </Text>
              <Line />
            </>
          )}

          {/* Items header */}
          <Row left={<Text bold>Items:</Text>} right={<Text bold>Qty</Text>} />
          <Line />

          {/* Items list for this prep station */}
          {station.items.map((e, i) => {
            const descWidth = 30;
            const productName = sanitizeForPrinter(e?.productName || "");
            const productLines = wrapTextByWords(productName, descWidth);

            return (
              <React.Fragment key={`item-${i}`}>
                {/* Main product name with quantity */}
                {productLines.map((line, idx) => (
                  <Row
                    key={`product-${i}-${idx}`}
                    left={<Text>{line}</Text>}
                    right={
                      idx === 0 ? (
                        <Text bold style={{ alignSelf: "flex-start" }}>
                          {e?.quantity}
                        </Text>
                      ) : (
                        <Text> </Text>
                      )
                    }
                  />
                ))}

                {/* Variant - wrapped */}
                {e?.variantName && (
                  <>
                    {wrapTextByWords(sanitizeForPrinter(`  VAR: ${e.variantName}`), descWidth).map(
                      (line, idx) => (
                        <Text key={`variant-${i}-${idx}`}>{line}</Text>
                      )
                    )}
                  </>
                )}

                {/* Add-ons - wrapped */}
                {e?.addonProduct?.length > 0 && (
                  <>
                    {wrapTextByWords(
                      sanitizeForPrinter(
                        `  ADD-ON: ${e.addonProduct
                          .map((a) => `${a?.itemName} x${a?.quantity || a?.qty || 1}`)
                          .join(", ")}`
                      ),
                      descWidth
                    ).map((line, idx) => (
                      <Text key={`addon-${i}-${idx}`}>{line}</Text>
                    ))}
                  </>
                )}

                {e?.comments && (
                  <>
                    {wrapTextByWords(
                      sanitizeForPrinter(`  COMMENTS: ${e.comments}`),
                      descWidth
                    ).map((line, idx) => (
                      <Text key={`item-comment-${i}-${idx}`}>{line}</Text>
                    ))}
                  </>
                )}

                <Text> </Text>
              </React.Fragment>
            );
          })}
          <Line />

          {/* Comments - wrapped */}
          {comments && (
            <>
              {wrapTextByWords(sanitizeForPrinter(`Comments: ${comments}`), 38).map((line, idx) => (
                <Text key={`comment-${idx}`}>{line}</Text>
              ))}
              <Line />
            </>
          )}

          {/* Partial cut after each prep station KOT */}
          <Cut type="partial" line={0} />
        </React.Fragment>
      ))}
    </>
  );
};

export const renderToken = (
  baseOrderResponse,
  tokenNumber,
  noGst,
  gst1slab,
  gst2slab,
  gst5slab,
  gst6slab,
  gst9slab,
  gst14slab,
  gst20slab,
  freeProducts
) => {
  const isInstant = baseOrderResponse?.slotId?.startsWith?.("IN");

  const slabs = [
    { items: noGst, key: "nogst" },
    { items: gst1slab, key: "gst1" },
    { items: gst2slab, key: "gst2" },
    { items: gst5slab, key: "gst5" },
    { items: gst6slab, key: "gst6" },
    { items: gst9slab, key: "gst9" },
    { items: gst14slab, key: "gst14" },
    { items: gst20slab, key: "gst20" },
    { items: freeProducts, key: "free" },
  ];

  // Collect all items from all slabs
  const allItems = slabs.flatMap(({ items }) => items || []);

  // Group items by prep station
  const itemsByPrepStation = allItems.reduce((acc, item) => {
    // If prepStationName is null, group all together as "no-station"
    const prepStationId = item?.prepStationName ? item?.prepStationId : "no-station";
    const prepStationName = item?.prepStationName || null;

    if (!acc[prepStationId]) {
      acc[prepStationId] = {
        name: prepStationName,
        items: [],
      };
    }

    acc[prepStationId].items.push(item);
    return acc;
  }, {});

  const prepStations = Object.values(itemsByPrepStation);

  const renderItemRow = (e, index) => (
    <React.Fragment key={`item-${index}-${e?.productId || Math.random()}`}>
      <Row
        left={<Text>{e?.productName || ""}</Text>}
        right={
          <Text bold style={{ alignSelf: "flex-start" }}>
            {e?.returnedQuantity ?? e?.quantityBySpecs ?? e?.quantity}
          </Text>
        }
      />
      {e?.variantName && <Text>{` VAR: ${e.variantName}`}</Text>}
      {e?.addonProduct?.length > 0 && (
        <Text>
          {`\x1bE\x01ADD-ON:\x1bE\x00 ${e.addonProduct
            .map((a) => `${a?.itemName} x${a?.quantity || a?.qty || 1}`)
            .join(", ")}`}
        </Text>
      )}
      {e?.comments && <Text>{`* ${e.comments}`}</Text>}
      <Text> </Text>
    </React.Fragment>
  );

  return (
    <>
      {prepStations.map((station, stationIndex) => (
        <React.Fragment key={`station-${stationIndex}`}>
          {/* Token header */}
          <Text align="center" bold size={{ width: 2, height: 2 }}>
            {`KOT:${tokenNumber}`}
          </Text>
          <Line />

          <Text>{`Order Id:${baseOrderResponse?.orderId || ""}`}</Text>

          {/* Slot Info */}
          {isInstant ? (
            <Text>Slot: Instant Delivery</Text>
          ) : baseOrderResponse?.startTime && baseOrderResponse?.endTime && formatSlot(baseOrderResponse?.startTime, baseOrderResponse?.endTime) ? (
            <Text>{formatSlot(baseOrderResponse?.startTime, baseOrderResponse?.endTime)}</Text>
          ) : null}

          <Row
            left={<Text>{`Date: ${new Date().toLocaleDateString()}`}</Text>}
            right={<Text>{`Time: ${new Date().toLocaleTimeString()}`}</Text>}
          />

          <Text>
            {`Service: ${baseOrderResponse?.subOrderType || baseOrderResponse?.orderType || "NA"}`}
          </Text>
          <Line />

          {/* Prep Station Name Header - only if not null */}
          {station.name && (
            <>
              <Text align="center" bold size={{ width: 1.5, height: 1.5 }}>
                Prep Station: {station.name}
              </Text>
              <Line />
            </>
          )}

          {/* Items header */}
          <Row left={<Text bold>Items:</Text>} right={<Text bold>Qty</Text>} />
          <Line />

          {/* Render items for this prep station */}
          {station.items.map((e, index) => renderItemRow(e, index))}

          <Line />

          {/* Comments */}
          {baseOrderResponse?.comments && <Text>{`Comments: ${baseOrderResponse?.comments}`}</Text>}

          {/* Partial cut after each prep station KOT */}
          <Cut type="partial" line={0} />
        </React.Fragment>
      ))}
    </>
  );
};

export const renderToken002 = (
  baseOrderResponse,
  tokenNumber,
  noGst,
  gst1slab,
  gst2slab,
  gst5slab,
  gst6slab,
  gst9slab,
  gst14slab,
  gst20slab,
  freeProducts
) => {
  const slabs = [
    { items: noGst, key: "nogst" },
    { items: gst1slab, key: "gst1" },
    { items: gst2slab, key: "gst2" },
    { items: gst5slab, key: "gst5" },
    { items: gst6slab, key: "gst6" },
    { items: gst9slab, key: "gst9" },
    { items: gst14slab, key: "gst14" },
    { items: gst20slab, key: "gst20" },
    { items: freeProducts, key: "free" },
  ];

  // Collect all items from all slabs
  const allItems = slabs.flatMap(({ items }) => items || []);

  // Group items by prep station
  const itemsByPrepStation = allItems.reduce((acc, item) => {
    // If prepStationName is null, group all together as "no-station"
    const prepStationId = item?.prepStationName ? item?.prepStationId : "no-station";
    const prepStationName = item?.prepStationName || null;

    if (!acc[prepStationId]) {
      acc[prepStationId] = {
        name: prepStationName,
        items: [],
      };
    }

    acc[prepStationId].items.push(item);
    return acc;
  }, {});

  const prepStations = Object.values(itemsByPrepStation);

  const renderItemRow = (e, index) => (
    <React.Fragment key={`item-${index}-${e?.productId || Math.random()}`}>
      <Row
        left={<Text>{e?.productName || ""}</Text>}
        right={
          <Text bold style={{ alignSelf: "flex-start" }}>
            {e?.returnedQuantity ?? e?.quantityBySpecs ?? e?.quantity}
          </Text>
        }
      />
      {e?.variantName && <Text>{` VAR: ${e.variantName}`}</Text>}
      {e?.addonProduct?.length > 0 && (
        <Text>
          {`\x1bE\x01ADD-ON:\x1bE\x00 ${e.addonProduct
            .map((a) => `${a?.itemName} x${a?.quantity || a?.qty || 1}`)
            .join(", ")}`}
        </Text>
      )}
      {e?.comments && <Text>{`* ${e.comments}`}</Text>}
      <Text> </Text>
    </React.Fragment>
  );

  return (
    <>
      {prepStations.map((station, stationIndex) => (
        <React.Fragment key={`station-${stationIndex}`}>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          <Text> </Text>
          {/* Token header */}
          <Text align="center" bold size={{ width: 2, height: 2 }}>
            {tokenNumber !== "" && tokenNumber !== null && tokenNumber !== undefined
              ? `KOT:${tokenNumber}`
              : `REPRINTED KOT`}
          </Text>
          <Line />

          <Text>{`Order Id:${baseOrderResponse?.orderId || ""}`}</Text>

          {/* Slot Info */}
          {baseOrderResponse?.slotId?.startsWith?.("IN") ? (
            <Text>Slot: Instant Delivery</Text>
          ) : baseOrderResponse?.startTime && baseOrderResponse?.endTime && formatSlot(baseOrderResponse?.startTime, baseOrderResponse?.endTime) ? (
            <Text>{formatSlot(baseOrderResponse?.startTime, baseOrderResponse?.endTime)}</Text>
          ) : null}

          <Row
            left={<Text>{`Date: ${new Date().toLocaleDateString()}`}</Text>}
            right={<Text>{`Time: ${new Date().toLocaleTimeString()}`}</Text>}
          />

          <Text>
            {`Service: ${baseOrderResponse?.subOrderType || baseOrderResponse?.orderType || "NA"}`}
          </Text>
          <Line />

          {/* Prep Station Name Header - only if not null */}
          {station.name && (
            <>
              <Text align="center" bold size={{ width: 1.5, height: 1.5 }}>
                Prep Station: {station.name}
              </Text>
              <Line />
            </>
          )}

          {/* Items header */}
          <Row left={<Text bold>Items:</Text>} right={<Text bold>Qty</Text>} />
          <Line />

          {/* Render items for this prep station */}
          {station.items.map((e, index) => renderItemRow(e, index))}

          <Line />

          {/* Comments */}
          {baseOrderResponse?.comments && <Text>{`Comments: ${baseOrderResponse?.comments}`}</Text>}

          {/* Partial cut after each prep station KOT */}
          <Cut type="partial" line={0} />
        </React.Fragment>
      ))}
    </>
  );
};
